import React, { useState, useEffect, useRef } from "react";
import {
    Box as MuiBox,
    Paper as MuiPaper,
    Typography as MuiTypography,
    IconButton as MuiIconButton,
    Chip as MuiChip,
    Tabs as MuiTabs,
    Tab as MuiTab,
    Card as MuiCard,
    CardContent as MuiCardContent,
    LinearProgress as MuiLinearProgress,
    CircularProgress as MuiCircularProgress,
    TextField as MuiTextField,
    TextareaAutosize as MuiTextareaAutosize,
    Button as MuiButton,
    FormControl as MuiFormControl,
    Select as MuiSelect,
    MenuItem as MuiMenuItem,
    ListItemText as MuiListItemText,
    Skeleton as MuiSkeleton,
    Alert as MuiAlert,
    Drawer as MuiDrawer,
    Tooltip as MuiTooltip,
    List as MuiList,
    ListItem as MuiListItem,
    ListItemIcon as MuiListItemIcon
} from "@mui/material";
import {
    Edit as EditIcon,
    GridView as GridViewIcon,
    ViewList as ViewListIcon,
    CloudUpload as CloudUploadIcon,
    Analytics as AnalyticsIcon,
    History as HistoryIcon,
    Code,
    Description as DescriptionIcon,
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
    ErrorOutline as ErrorOutlineIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon
} from "@mui/icons-material";

import SDLCMatrix from "./SDLCMatrix";
import WorkProductCard from "./WorkProductCard";
import { UploadCenter } from "./UploadCenter";
import TargetArrow from "../imports/TargetArrow-16-14";
import BulbIcon from "../imports/Vector-2005-15";
import DirectionsIcon from "../imports/Directions-2005-76";
import ChecklistIcon from "../imports/Checklist-2005-107";
import RocketIcon from "../imports/Vector";
import { getSDLCIconStyle } from "../utils/appConstants";
import ProjectSetupChatbotModal from "./ProjectSetupChatbotModal";
import { ProjectSetupData } from "./AIProjectSetupModal";
import { useAppSelector, useAppDispatch } from "@/state/hooks";
import {
    fetchProjectFeaturesApi,
    exportSddProgressReport,
    getProjectFeatureHierarchyApi,
    getSddProgress,
    getSddProgressReportPage,
    getWorkProductsByPhase,
    updateFeatureApi,
    type SddProgressReport
} from "@/api/api";
import { Feature } from "@/api/models/feature";
import { fetchWorkProductsByPhase } from "@/state/workproduct/workProductSlice";
import { fetchProjects } from "@/state/project/projectSlice";
import { fetchFeatureUserStories } from "@/state/userStories/userStoriesSlice";
import { WorkProduct } from "../types";
import { FeatureFlag } from "@/features";
import { GuidedTiersNav } from "./feature-studio/GuidedTiersNav";
import { FeatureStudioDetailHeader } from "./feature-studio/FeatureStudioDetailHeader";
import { formatStoryHeaderTitle } from "./feature-studio/storyHeader";
import { FeatureStudioStats } from "./feature-studio/FeatureStudioStats";
import { FeatureStudioCard } from "./feature-studio/FeatureStudioCard";
import { SddWorkspace } from "./feature-studio/SddWorkspace";
import { findFeatureBySelectedId, getSelectedFeatureId } from "./feature-studio/featureSelection";
import { SyncPanel } from "@/components/sync/SyncPanel";
import { mapApiToWorkProducts } from "../utils/helpers";
import { MeshStrengthEntryCard } from "./meshStrength/MeshStrengthEntryCard";
import { useBackgroundActivity } from "./backgroundActivity";
import { useOptionalBatchGeneration, type BatchReadiness, type TrackedBatch } from "./batchGeneration";
import { isActiveProductBatch } from "./products/ProductBatchGenerationStatus";
import { selectLiveWorkProductProgress } from "./feature-studio/liveWorkProductProgress";
import { NavigationBackButton } from "./NavigationBackButton";

// Temporarily hide the hierarchy rail without removing its state, controls, or implementation.
const SHOW_HIERARCHY_RAIL = false;
const LAPTOP_FIT_QUERY = "@media (min-width: 1280px) and (min-height: 768px)";

export interface OptionalWorkProductScope {
    projectId: number | null;
    feature: any | null;
    story: any | null;
}

type SddWorkProductScope = {
    level: "PRODUCT" | "FEATURE" | "USERSTORY";
    featureId: number | null;
    userStoryId: string | null;
};

function patchScopedApproval(
    catalog: Record<string, WorkProduct[]> | null,
    workProductId: string,
    scope: SddWorkProductScope,
    approved: boolean
): Record<string, WorkProduct[]> | null {
    if (!catalog) return catalog;
    return Object.fromEntries(
        Object.entries(catalog).map(([phase, workProducts]) => [
            phase,
            workProducts.map(workProduct => {
                if (String(workProduct.id) !== String(workProductId)) return workProduct;
                if (workProduct.scope_level && workProduct.scope_level !== scope.level) return workProduct;
                if (workProduct.scope_level === "FEATURE" && Number(workProduct.feature_id) !== scope.featureId) return workProduct;
                if (
                    workProduct.scope_level === "USERSTORY" &&
                    (Number(workProduct.feature_id) !== scope.featureId || String(workProduct.user_story_id) !== scope.userStoryId)
                ) {
                    return workProduct;
                }
                return { ...workProduct, approved };
            })
        ])
    );
}

interface FeatureStudioViewProps {
    isMobile: boolean;
    selectedProject: any | null; // Can be either Initiative (mock) or Projects (real DB)
    initiatives: any[];
    selectedFeature: any;
    isHierarchyCollapsed: boolean;
    featureStudioTab: string;
    expandedAccordions: Set<string>;
    workProductsByPhase: Record<string, WorkProduct[]>;
    expandedWorkProducts: Set<string>;
    generatingWorkProducts: Set<string>;
    processingWorkProducts: Set<string>;
    workProductProgress: Map<string, any>;
    isFeatureInfoEditing: boolean;
    featureInfoData: any;
    featuresRefreshTrigger?: number; // Add refresh trigger
    workProductRefreshTrigger?: number; // Add work product refresh trigger for navigation back
    workProductRefreshScope?: SddWorkProductScope | null;
    selectedUserStory?: any | null; // Add selected user story
    hierarchyExpandedItems: string[]; // Add hierarchy expanded items
    onHierarchyExpandedItemsChange: (items: string[]) => void; // Add handler for hierarchy expanded items change
    onBackToProjects: () => void;
    onAddFeature: () => void;
    onAddUserStory: () => void; // Add handler for adding user story
    onCreateFromHierarchy?: (context: { project: any; feature?: any }) => void;
    onHierarchyToggle: () => void;
    onFeatureSelect: (feature: any) => void;
    onUserStorySelect?: (userStory: any) => void; // Add user story selection handler
    optionalWorkProductScope: OptionalWorkProductScope;
    onOptionalWorkProductScopeChange: (scope: OptionalWorkProductScope) => void;
    onAccordionToggle: (id: string) => void;
    onFeatureInfoEdit: () => void;
    onGenerateWithAI: () => void;
    onFeatureInfoData: (data: any) => void;
    handleWorkProductSelect: (
        wp: WorkProduct,
        scope?: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null }
    ) => void;
    handleWorkProductGenerate: (
        wp: WorkProduct,
        projectId?: number,
        options?: { forceNewVersion?: boolean; scope?: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null } }
    ) => Promise<unknown> | void;
    handleAddWorkProductToEditable: (wp: WorkProduct, projectId?: number) => void;
    handleToggleExpanded: (id: string) => void;
    handleApprovalToggle: (
        workProduct: WorkProduct,
        approved: boolean,
        scope?: SddWorkProductScope
    ) => Promise<boolean> | boolean;
    handleCancelWorkProductProgress: (id: string) => void;
    onFeaturePromote?: (featureData: any) => void;
    onEditFeature?: (feature: any) => void;
    onDeleteFeature?: (feature: any) => void;
    onEditUserStory?: (feature: any, userStory: any) => void;
    onDeleteUserStory?: (userStory: any) => void;
    deletingFeatureId?: string | null; // Track which feature is being deleted
    onFeatureDeleteComplete?: () => void; // Callback when feature deletion refresh is complete
    /** Navigate to the Integrations view — wired to SyncPanel's "Reconnect" CTA (see Task 16). */
    onNavigateToIntegrations?: () => void;
    onOpenMeshStrength?: () => void;
}

// Work Product State Machine
type WorkProductState =
    | { state: "idle" }
    | { state: "generating"; step?: string; progress?: number | null }
    | { state: "ready"; updated: string; preview: string; artifactId?: string };

import { IntelligenicEditorExample } from "@/components/IntelligenicEditor";
import { FontSize } from "@/components/tiptap";

const getSDLCIcon = (phaseId: string) => {
    const iconWrapperStyle = getSDLCIconStyle();

    switch (phaseId) {
        case "discovery":
            return (
                <div style={iconWrapperStyle}>
                    <DirectionsIcon />
                </div>
            );
        case "design":
            return (
                <div style={iconWrapperStyle}>
                    <BulbIcon />
                </div>
            );
        case "code":
            return (
                <div style={iconWrapperStyle}>
                    <Code sx={{ color: "#103699", fontSize: "20px" }} />
                </div>
            );
        case "qa":
            return (
                <div style={iconWrapperStyle}>
                    <ChecklistIcon />
                </div>
            );
        case "deploy":
            return (
                <div style={iconWrapperStyle}>
                    <RocketIcon />
                </div>
            );
        default:
            return (
                <div style={iconWrapperStyle}>
                    <DescriptionIcon sx={{ color: "#103699", fontSize: "20px" }} />
                </div>
            );
    }
};

/**
 * Calculate stats for the 4-card strip.
 * All levels share the same metrics: Work Products, Available, Ready, Generating.
 */
const calculateStats = (
    level: "product" | "feature" | "story",
    product: any,
    feature: any | null,
    story: any | null,
    workProductsByPhase: Record<string, any[]>,
    generatingWorkProducts: Set<string>
): {
    card1: { label: string; value: number };
    card2: { label: string; value: number };
    ready: number;
    generating: number;
} => {
    const featureId = feature?.featureid || feature?.id;
    const storyId = story?.id;

    // Use the same Discovery card filter as the rendered card grid so stats match what users see.
    const discoveryWorkProducts = (workProductsByPhase.discovery || [])
        .filter(wp => wp.state === "editable" || wp.state === "available-to-add")
        .filter(wp => !wp.name.includes("(HIDDEN)"));

    const discoveryStateCounts = discoveryWorkProducts.reduce(
        (acc, wp) => {
            const wpKey = generateWorkProductKey(wp.id, product?.project_id, featureId, storyId);

            // Keep state precedence aligned with getWorkProductState:
            // generating > ready > available.
            if (generatingWorkProducts.has(wpKey)) {
                acc.generating += 1;
            } else if (wp.hasContent || wp.state === "editable") {
                acc.ready += 1;
            } else {
                acc.available += 1;
            }

            return acc;
        },
        { ready: 0, generating: 0, available: 0 }
    );

    // `level` is currently ignored by design so all contexts show the same 4 metric types.
    void level;
    const totalWPs = discoveryWorkProducts.length;

    return {
        card1: { label: "Work Products", value: totalWPs },
        card2: { label: "Available", value: discoveryStateCounts.available },
        ready: discoveryStateCounts.ready,
        generating: discoveryStateCounts.generating
    };
};

/**
 * Generate a composite key for tracking work product generation state.
 * This ensures that work products with the same template ID across different features/user stories/products
 * are tracked separately.
 *
 * Format:
 * - User Story level: "us-{userStoryId}-wp-{workProductId}"
 * - Feature level: "f-{featureId}-wp-{workProductId}"
 * - Product level: "p-{projectId}-wp-{workProductId}"
 */
const generateWorkProductKey = (workProductId: string, projectId?: number | null, featureId?: number | string | null, userStoryId?: string | null): string => {
    if (userStoryId) {
        return `us-${userStoryId}-wp-${workProductId}`;
    }
    if (featureId) {
        return `f-${featureId}-wp-${workProductId}`;
    }
    // Product level (no feature or user story) - include project ID to differentiate between products
    if (projectId) {
        return `p-${projectId}-wp-${workProductId}`;
    }
    // Fallback if no context is available (shouldn't happen in normal flow)
    return `wp-${workProductId}`;
};

// Convert to a proper React component to use hooks correctly - memoized to prevent re-renders
const TabContent: React.FC<
    FeatureStudioViewProps & {
        currentFeature: Feature | null;
        isLoadingFeature: boolean;
        workProductsByPhase: Record<string, WorkProduct[]>;
        isLoadingWorkProducts: boolean;
        validatedProject: any;
        viewMode: "list" | "grid";
        onViewModeChange: (mode: "list" | "grid") => void;
        getWorkProductState: (nodeKey: string, wpId: string) => WorkProductState;
        handleCardGenerate: (
            wp: WorkProduct,
            scope?: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null }
        ) => Promise<unknown> | void;
        onSyncClick: (featureId: number) => void;
        handleCardOpen: (wp: WorkProduct, scope?: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null }) => void;
        selectedNodeKey: string;
        hierarchyFeatures: any[];
        hierarchyStoriesByFeature: Record<number, any[]>;
        sddHierarchyPageOpen: boolean;
        onSddHierarchyPageOpenChange: (open: boolean) => void;
        sddScopePickerOpen: boolean;
        onSddScopePickerOpenChange: (open: boolean) => void;
        sddProgressReportOpen: boolean;
        onSddProgressReportOpenChange: (open: boolean) => void;
    }
> = props => {
    // Hooks must be called at the top level, before any conditional logic
    const token = useAppSelector(state => state.clientToken.token);
    const tabDispatch = useAppDispatch();
    const { jobs: tabBackgroundJobs } = useBackgroundActivity();

    const {
        featureStudioTab,
        selectedFeature,
        selectedUserStory,
        currentFeature,
        isLoadingFeature,
        workProductsByPhase,
        isLoadingWorkProducts,
        expandedAccordions,
        onAccordionToggle,
        expandedWorkProducts,
        generatingWorkProducts,
        processingWorkProducts,
        handleWorkProductSelect,
        handleWorkProductGenerate,
        handleAddWorkProductToEditable,
        handleToggleExpanded,
        handleApprovalToggle,
        handleCancelWorkProductProgress,
        workProductProgress,
        isFeatureInfoEditing,
        featureInfoData,
        onFeatureInfoEdit,
        onGenerateWithAI,
        onFeatureInfoData,
        validatedProject,
        viewMode,
        onViewModeChange,
        getWorkProductState,
        handleCardGenerate,
        onSyncClick,
        handleCardOpen,
        selectedNodeKey,
        hierarchyFeatures,
        hierarchyStoriesByFeature,
        sddHierarchyPageOpen,
        onSddHierarchyPageOpenChange,
        sddScopePickerOpen,
        onSddScopePickerOpenChange,
        sddProgressReportOpen,
        onSddProgressReportOpenChange,
        optionalWorkProductScope,
        onOptionalWorkProductScopeChange
    } = props;

    const optionalProjectId = Number(validatedProject?.project_id || validatedProject?.id || 0);
    const optionalScopeMatchesProject = optionalWorkProductScope.projectId === optionalProjectId;
    const optionalFeatureScope = optionalScopeMatchesProject ? optionalWorkProductScope.feature : null;
    const optionalStoryScope = optionalScopeMatchesProject ? optionalWorkProductScope.story : null;
    const [optionalWorkProducts, setOptionalWorkProducts] = useState<Record<string, WorkProduct[]> | null>(null);
    const [optionalWorkProductsLoading, setOptionalWorkProductsLoading] = useState(false);
    const [requiredWorkProducts, setRequiredWorkProducts] = useState<Record<string, WorkProduct[]> | null>(null);
    const [sddProgress, setSddProgress] = useState<SddProgressReport | null>(null);
    const [sddProgressLoading, setSddProgressLoading] = useState(false);
    const [completeProductHierarchy, setCompleteProductHierarchy] = useState<any[] | null>(null);
    const optionalRequestId = useRef(0);
    const requiredRequestId = useRef(0);
    const optionalGenerationScopes = useRef(
        new Map<
            string,
            {
                observed: boolean;
                featureScope: any | null;
                storyScope: any | null;
            }
        >()
    );
    const requiredGenerationScopes = useRef(new Map<string, { observed: boolean }>());
    const generatingWorkProductsRef = useRef(generatingWorkProducts);
    generatingWorkProductsRef.current = generatingWorkProducts;
    const [, setOptionalGenerationRevision] = useState(0);
    const [, setRequiredGenerationRevision] = useState(0);
    const optionalLiveProgress = React.useMemo(
        () => selectLiveWorkProductProgress(tabBackgroundJobs, optionalProjectId),
        [optionalProjectId, tabBackgroundJobs]
    );
    const previousOptionalLiveIds = useRef(new Set<string>());
    const progressRefreshKey = React.useMemo(
        () =>
            Object.values(workProductsByPhase)
                .flat()
                .map(workProduct => `${workProduct.id}:${Boolean(workProduct.hasContent)}:${Boolean(workProduct.approved)}`)
                .sort()
                .join("|"),
        [workProductsByPhase]
    );

    const loadSddProgress = React.useCallback(async () => {
        if (!token || !optionalProjectId) {
            setSddProgress(null);
            return;
        }
        setSddProgressLoading(true);
        try {
            setSddProgress(await getSddProgress(token, optionalProjectId));
        } catch (error) {
            console.error("Failed to load SDD progress:", error);
            setSddProgress(null);
        } finally {
            setSddProgressLoading(false);
        }
    }, [optionalProjectId, token]);

    const loadSddProgressReport = React.useCallback(
        async (filters: Parameters<typeof getSddProgressReportPage>[2], page: number, pageSize: number) => {
            if (!token || !optionalProjectId) throw new Error("A signed-in product session is required.");
            return getSddProgressReportPage(token, optionalProjectId, filters, page, pageSize);
        },
        [optionalProjectId, token]
    );

    const downloadSddProgressReport = React.useCallback(
        async (filters: Parameters<typeof exportSddProgressReport>[2]) => {
            if (!token || !optionalProjectId) throw new Error("A signed-in product session is required.");
            return exportSddProgressReport(token, optionalProjectId, filters);
        },
        [optionalProjectId, token]
    );

    React.useEffect(() => {
        if (featureStudioTab !== "sdd") return;
        void loadSddProgress();
    }, [featureStudioTab, loadSddProgress, progressRefreshKey, props.workProductRefreshTrigger]);

    const loadCompleteProductHierarchy = React.useCallback(async () => {
        if (!token || !optionalProjectId) {
            setCompleteProductHierarchy(null);
            return;
        }
        try {
            const hierarchy = await getProjectFeatureHierarchyApi(optionalProjectId, token);
            setCompleteProductHierarchy(Array.isArray(hierarchy.features) ? hierarchy.features : null);
        } catch (error) {
            console.error("Failed to load complete product hierarchy:", error);
            setCompleteProductHierarchy(null);
        }
    }, [optionalProjectId, token]);

    React.useEffect(() => {
        if (featureStudioTab !== "sdd") return;
        void loadCompleteProductHierarchy();
    }, [featureStudioTab, loadCompleteProductHierarchy, props.featuresRefreshTrigger]);

    const requiredFeature = selectedFeature || currentFeature;
    const requiredFeatureId = Number(requiredFeature?.featureid || requiredFeature?.id || 0) || null;
    const requiredStoryId = selectedUserStory?.id ? String(selectedUserStory.id) : null;

    const loadRequiredWorkProducts = React.useCallback(async () => {
        if (!token || !optionalProjectId) {
            setRequiredWorkProducts(null);
            return;
        }

        const requestId = ++requiredRequestId.current;
        setRequiredWorkProducts({});
        const scopes: Array<{
            level: "PRODUCT" | "FEATURE" | "USERSTORY";
            featureId?: number;
            storyId?: string;
        }> = [
            { level: "PRODUCT" },
            { level: "FEATURE", featureId: requiredFeatureId || undefined },
            {
                level: "USERSTORY",
                featureId: requiredFeatureId || undefined,
                storyId: requiredStoryId || undefined
            }
        ];

        const responses = await Promise.allSettled(
            scopes.map(async scope => {
                const workProducts = mapApiToWorkProducts(
                    await getWorkProductsByPhase(token, optionalProjectId, scope.featureId, scope.storyId, scope.level)
                );
                if (requestId !== requiredRequestId.current) return;

                // Publish each hierarchy catalog independently. Unselected
                // Feature and Story templates still supply backend-owned hover
                // descriptions, while scope checks keep their actions disabled.
                setRequiredWorkProducts(current => {
                    const combined: Record<string, WorkProduct[]> = {
                        discovery: [...(current?.discovery || [])],
                        design: [...(current?.design || [])],
                        "task-breakdown": [...(current?.["task-breakdown"] || [])],
                        code: [...(current?.code || [])],
                        qa: [...(current?.qa || [])],
                        deploy: [...(current?.deploy || [])]
                    };
                    Object.entries(workProducts).forEach(([phase, products]) => {
                        if (!combined[phase]) combined[phase] = [];
                        combined[phase].push(
                            ...products.map(workProduct => {
                                const resolvedScopeLevel = String(workProduct.scope_level || scope.level || "").toUpperCase();
                                return {
                                    ...workProduct,
                                    scope_level: workProduct.scope_level || scope.level,
                                    feature_id:
                                        resolvedScopeLevel === "PRODUCT"
                                            ? workProduct.feature_id
                                            : workProduct.feature_id ?? scope.featureId,
                                    user_story_id:
                                        resolvedScopeLevel !== "USERSTORY"
                                            ? workProduct.user_story_id
                                            : workProduct.user_story_id ?? scope.storyId
                                };
                            })
                        );
                    });
                    return combined;
                });
            })
        );
        if (requestId !== requiredRequestId.current) return;

        const failures = responses.filter(response => response.status === "rejected");
        failures.forEach(response => {
            if (response.status === "rejected") {
                console.error("Failed to load an SDD hierarchy work-product catalog:", response.reason);
            }
        });
        if (failures.length === responses.length) {
            setRequiredWorkProducts(null);
        }
    }, [optionalProjectId, requiredFeatureId, requiredStoryId, token]);

    React.useEffect(() => {
        if (featureStudioTab !== "sdd") return;
        void loadRequiredWorkProducts();
    }, [featureStudioTab, loadRequiredWorkProducts, progressRefreshKey, props.workProductRefreshTrigger]);

    const loadOptionalWorkProducts = React.useCallback(
        async (featureScope: any | null, storyScope: any | null) => {
            if (!token || !optionalProjectId) {
                setOptionalWorkProducts(null);
                return;
            }

            const requestId = ++optionalRequestId.current;
            const featureId = Number(featureScope?.featureid || featureScope?.id || 0) || undefined;
            const storyId = storyScope?.id ? String(storyScope.id) : undefined;
            const scopes: Array<{
                level: "PRODUCT" | "FEATURE" | "USERSTORY";
                featureId?: number;
                storyId?: string;
            }> = [{ level: "PRODUCT" }];
            if (featureId) scopes.push({ level: "FEATURE", featureId });
            if (featureId && storyId) scopes.push({ level: "USERSTORY", featureId, storyId });
            setOptionalWorkProductsLoading(true);
            try {
                const responses = await Promise.allSettled(
                    scopes.map(async scope => ({
                        scope,
                        workProducts: mapApiToWorkProducts(
                            await getWorkProductsByPhase(token, optionalProjectId, scope.featureId, scope.storyId, scope.level)
                        )
                    }))
                );
                if (requestId !== optionalRequestId.current) return;

                const combined: Record<string, WorkProduct[]> = {
                    discovery: [],
                    design: [],
                    "task-breakdown": [],
                    code: [],
                    qa: [],
                    deploy: []
                };
                responses.forEach(response => {
                    if (response.status === "rejected") {
                        console.error("Failed to load an optional SDD hierarchy work-product catalog:", response.reason);
                        return;
                    }
                    const { scope, workProducts } = response.value;
                    Object.entries(workProducts).forEach(([phase, products]) => {
                        if (!combined[phase]) combined[phase] = [];
                        combined[phase].push(
                            ...products.map(workProduct => {
                                const resolvedScopeLevel = String(workProduct.scope_level || scope.level || "").toUpperCase();
                                return {
                                    ...workProduct,
                                    scope_level: workProduct.scope_level || scope.level,
                                    feature_id:
                                        resolvedScopeLevel === "PRODUCT"
                                            ? workProduct.feature_id
                                            : workProduct.feature_id ?? scope.featureId,
                                    user_story_id:
                                        resolvedScopeLevel !== "USERSTORY"
                                            ? workProduct.user_story_id
                                            : workProduct.user_story_id ?? scope.storyId
                                };
                            })
                        );
                    });
                });
                setOptionalWorkProducts(combined);
            } catch (error) {
                if (requestId === optionalRequestId.current) {
                    console.error("Failed to load independently scoped optional work products:", error);
                    setOptionalWorkProducts({});
                }
            } finally {
                if (requestId === optionalRequestId.current) setOptionalWorkProductsLoading(false);
            }
        },
        [optionalProjectId, token]
    );

    React.useEffect(() => {
        if (featureStudioTab !== "sdd") return;
        void loadOptionalWorkProducts(optionalFeatureScope, optionalStoryScope);
    }, [featureStudioTab, loadOptionalWorkProducts, optionalFeatureScope, optionalStoryScope, props.workProductRefreshTrigger]);

    const optionalFeatureId = Number(optionalFeatureScope?.featureid || optionalFeatureScope?.id || 0);
    React.useEffect(() => {
        if (featureStudioTab !== "sdd" || !optionalFeatureId || !token || optionalFeatureId in hierarchyStoriesByFeature) return;
        void tabDispatch(fetchFeatureUserStories({ featureId: optionalFeatureId, idToken: token }));
    }, [featureStudioTab, hierarchyStoriesByFeature, optionalFeatureId, tabDispatch, token]);

    const optionalFeatureWithStories = optionalFeatureScope
        ? {
              ...optionalFeatureScope,
              user_stories:
                  hierarchyStoriesByFeature[optionalFeatureId] ||
                  optionalFeatureScope.user_stories ||
                  optionalFeatureScope.userStories ||
                  optionalFeatureScope.stories ||
                  []
          }
        : null;

    const handleOptionalFeatureSelect = React.useCallback(
        (selected: any | null) => {
            onOptionalWorkProductScopeChange({
                projectId: optionalProjectId || null,
                feature: selected,
                story: null
            });
        },
        [onOptionalWorkProductScopeChange, optionalProjectId]
    );

    const handleOptionalStorySelect = React.useCallback(
        (selected: any | null, selectedFeature?: any | null) => {
            onOptionalWorkProductScopeChange({
                projectId: optionalProjectId || null,
                feature: selectedFeature === undefined ? optionalFeatureScope : selectedFeature,
                story: selected
            });
        },
        [onOptionalWorkProductScopeChange, optionalFeatureScope, optionalProjectId]
    );

    const handleOptionalGenerate = React.useCallback(
        (workProduct: WorkProduct, scope: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null }) => {
            if (!optionalProjectId) return;
            const workProductKey = generateWorkProductKey(workProduct.id, optionalProjectId, scope.featureId, scope.userStoryId);
            if (generatingWorkProducts.has(workProductKey) || optionalGenerationScopes.current.has(workProductKey)) return;
            optionalGenerationScopes.current.set(workProductKey, {
                observed: false,
                featureScope: optionalFeatureScope,
                storyScope: optionalStoryScope
            });
            setOptionalGenerationRevision(revision => revision + 1);
            const submission = handleWorkProductGenerate(workProduct, optionalProjectId, { scope });
            void Promise.resolve(submission).then(() => {
                window.setTimeout(() => {
                    const tracked = optionalGenerationScopes.current.get(workProductKey);
                    if (!tracked || tracked.observed || generatingWorkProductsRef.current.has(workProductKey)) return;
                    optionalGenerationScopes.current.delete(workProductKey);
                    setOptionalGenerationRevision(revision => revision + 1);
                }, 1000);
            });
        },
        [generatingWorkProducts, handleWorkProductGenerate, optionalFeatureScope, optionalProjectId, optionalStoryScope]
    );

    const handleRequiredGenerate = React.useCallback(
        (workProduct: WorkProduct, scope?: SddWorkProductScope) => {
            if (!optionalProjectId || !scope) return;
            const workProductKey = generateWorkProductKey(workProduct.id, optionalProjectId, scope.featureId, scope.userStoryId);
            if (generatingWorkProducts.has(workProductKey) || requiredGenerationScopes.current.has(workProductKey)) return;

            requiredGenerationScopes.current.set(workProductKey, { observed: false });
            setRequiredGenerationRevision(revision => revision + 1);
            const submission = handleCardGenerate(workProduct, scope);
            void Promise.resolve(submission).then(() => {
                window.setTimeout(() => {
                    const tracked = requiredGenerationScopes.current.get(workProductKey);
                    if (!tracked || tracked.observed || generatingWorkProductsRef.current.has(workProductKey)) return;
                    requiredGenerationScopes.current.delete(workProductKey);
                    setRequiredGenerationRevision(revision => revision + 1);
                }, 1000);
            });
        },
        [generatingWorkProducts, handleCardGenerate, optionalProjectId]
    );

    React.useEffect(() => {
        let changed = false;
        const completedScopes: Array<{ featureScope: any | null; storyScope: any | null }> = [];
        optionalGenerationScopes.current.forEach((tracked, key) => {
            if (generatingWorkProducts.has(key)) {
                tracked.observed = true;
                return;
            }
            if (!tracked.observed) return;
            optionalGenerationScopes.current.delete(key);
            completedScopes.push(tracked);
            changed = true;
        });
        if (changed) setOptionalGenerationRevision(revision => revision + 1);

        const currentFeatureId = Number(optionalFeatureScope?.featureid || optionalFeatureScope?.id || 0) || null;
        const currentStoryId = optionalStoryScope?.id ? String(optionalStoryScope.id) : null;
        if (
            completedScopes.some(tracked => {
                const completedFeatureId = Number(tracked.featureScope?.featureid || tracked.featureScope?.id || 0) || null;
                const completedStoryId = tracked.storyScope?.id ? String(tracked.storyScope.id) : null;
                return completedFeatureId === currentFeatureId && completedStoryId === currentStoryId;
            })
        ) {
            void loadOptionalWorkProducts(optionalFeatureScope, optionalStoryScope);
        }
    }, [generatingWorkProducts, loadOptionalWorkProducts, optionalFeatureScope, optionalStoryScope]);

    React.useEffect(() => {
        let changed = false;
        let completed = false;
        requiredGenerationScopes.current.forEach((tracked, key) => {
            if (generatingWorkProducts.has(key)) {
                tracked.observed = true;
                return;
            }
            if (!tracked.observed) return;
            requiredGenerationScopes.current.delete(key);
            changed = true;
            completed = true;
        });
        if (changed) setRequiredGenerationRevision(revision => revision + 1);
        if (completed) {
            void loadRequiredWorkProducts();
            void loadSddProgress();
        }
    }, [generatingWorkProducts, loadRequiredWorkProducts, loadSddProgress]);

    const previousGeneratingKeysRef = React.useRef<Set<string>>(new Set());
    React.useEffect(() => {
        const previous = previousGeneratingKeysRef.current;
        const current = new Set(generatingWorkProducts);
        const completedKeys = Array.from(previous).filter(key => !current.has(key));
        previousGeneratingKeysRef.current = current;

        if (featureStudioTab !== "sdd" || completedKeys.length === 0) {
            return;
        }

        // Chat-triggered generation can finish without going through the
        // workspace-local tracking maps above. When any observed generation key
        // transitions out of the active set, refresh SDD catalogs and progress
        // so action buttons can switch from Build to View/Edit promptly.
        void loadRequiredWorkProducts();
        void loadOptionalWorkProducts(optionalFeatureScope, optionalStoryScope);
        void loadSddProgress();
    }, [
        featureStudioTab,
        generatingWorkProducts,
        loadOptionalWorkProducts,
        loadRequiredWorkProducts,
        loadSddProgress,
        optionalFeatureScope,
        optionalStoryScope,
    ]);

    React.useEffect(() => {
        const visibleOptionalIds = new Set(
            Object.values(optionalWorkProducts || {})
                .flat()
                .map(workProduct => String(workProduct.id))
        );
        const currentLiveIds = new Set(Array.from(optionalLiveProgress.keys()).filter(workProductId => visibleOptionalIds.has(workProductId)));
        const completedWhileMounted = Array.from(previousOptionalLiveIds.current).some(workProductId => !currentLiveIds.has(workProductId));
        previousOptionalLiveIds.current = currentLiveIds;
        if (completedWhileMounted) void loadOptionalWorkProducts(optionalFeatureScope, optionalStoryScope);
    }, [loadOptionalWorkProducts, optionalFeatureScope, optionalLiveProgress, optionalStoryScope, optionalWorkProducts]);

    const isScopeCompatibleForReadyFallback = React.useCallback(
        (
            scope: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null },
            candidate: WorkProduct
        ): boolean => {
            const declaredScope = String(candidate.scope_level || candidate.level || "").toUpperCase();
            if (declaredScope === "PRODUCT") return scope.level === "PRODUCT";
            if (declaredScope === "FEATURE") {
                return scope.level === "FEATURE" && Number(candidate.feature_id || 0) === Number(scope.featureId || 0);
            }
            if (declaredScope === "USERSTORY") {
                return (
                    scope.level === "USERSTORY" &&
                    Number(candidate.feature_id || 0) === Number(scope.featureId || 0) &&
                    String(candidate.user_story_id || "") === String(scope.userStoryId || "")
                );
            }

            if (scope.level === "PRODUCT") return !candidate.feature_id && !candidate.user_story_id;
            if (scope.level === "FEATURE") return Number(candidate.feature_id || 0) === Number(scope.featureId || 0) && !candidate.user_story_id;
            return (
                Number(candidate.feature_id || 0) === Number(scope.featureId || 0) &&
                String(candidate.user_story_id || "") === String(scope.userStoryId || "")
            );
        },
        []
    );

    const hasOptimisticReadyFallback = React.useCallback(
        (
            workProduct: WorkProduct,
            scope: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null }
        ): boolean => {
            return Object.values(props.workProductsByPhase)
                .flat()
                .some(candidate => {
                    if (String(candidate.id) !== String(workProduct.id)) return false;
                    if (!isScopeCompatibleForReadyFallback(scope, candidate)) return false;
                    return Boolean(candidate.hasContent || candidate.state === "editable");
                });
        },
        [isScopeCompatibleForReadyFallback, props.workProductsByPhase]
    );

    const getOptionalWorkProductState = React.useCallback(
        (
            workProduct: WorkProduct,
            scope: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null }
        ): WorkProductState => {
            const key = generateWorkProductKey(workProduct.id, optionalProjectId, scope.featureId, scope.userStoryId);
            const locallyTracked = optionalGenerationScopes.current.has(key);
            const live = optionalLiveProgress.get(String(workProduct.id));
            const progress = workProductProgress.get(key);
            if (locallyTracked || generatingWorkProducts.has(key) || live) {
                return {
                    state: "generating",
                    step: live?.message || progress?.step || progress?.message || "Starting generation…",
                    progress: live?.progress ?? progress?.progress ?? null
                };
            }
            if (workProduct.hasContent || workProduct.state === "editable" || hasOptimisticReadyFallback(workProduct, scope)) {
                return {
                    state: "ready",
                    updated: workProduct.lastModified instanceof Date ? workProduct.lastModified.toISOString() : workProduct.lastModified || "Today",
                    preview: workProduct.description || `${workProduct.name} has been generated. Click to open and refine.`
                };
            }
            return { state: "idle" };
        },
        [generatingWorkProducts, hasOptimisticReadyFallback, optionalLiveProgress, optionalProjectId, workProductProgress]
    );

    const getRequiredWorkProductState = React.useCallback(
        (
            workProduct: WorkProduct,
            scope?: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null }
        ): WorkProductState => {
            const key = generateWorkProductKey(workProduct.id, optionalProjectId, scope?.featureId, scope?.userStoryId);
            const progress = workProductProgress.get(key);
            if (requiredGenerationScopes.current.has(key) || generatingWorkProducts.has(key)) {
                const live = optionalLiveProgress.get(String(workProduct.id));
                return {
                    state: "generating",
                    step: live?.message || progress?.step || progress?.message || "Starting generation…",
                    progress: live?.progress ?? progress?.progress ?? null
                };
            }
            if (workProduct.hasContent || workProduct.state === "editable" || hasOptimisticReadyFallback(workProduct, scope || { level: "PRODUCT", featureId: null, userStoryId: null })) {
                return {
                    state: "ready",
                    updated: workProduct.lastModified instanceof Date ? workProduct.lastModified.toISOString() : workProduct.lastModified || "Today",
                    preview: workProduct.description || `${workProduct.name} has been generated. Click to open and refine.`
                };
            }
            return { state: "idle" };
        },
        [generatingWorkProducts, hasOptimisticReadyFallback, optionalLiveProgress, optionalProjectId, workProductProgress]
    );

    const handleSddApproval = React.useCallback(
        async (workProduct: WorkProduct, approved: boolean, scope: SddWorkProductScope): Promise<boolean> => {
            const persisted = await handleApprovalToggle(workProduct, approved, scope);
            if (persisted === false) return false;

            setRequiredWorkProducts(current => patchScopedApproval(current, workProduct.id, scope, approved));
            setOptionalWorkProducts(current => patchScopedApproval(current, workProduct.id, scope, approved));
            return true;
        },
        [handleApprovalToggle]
    );

    /* case "test-editor":
       return <IntelligenicEditorExample />; */

    switch (featureStudioTab) {
        case "sdd": {
            const workspaceFeature = selectedFeature || currentFeature;
            const workspaceFeatureId = Number(workspaceFeature?.id || workspaceFeature?.featureid);
            const workspaceStories =
                hierarchyStoriesByFeature[workspaceFeatureId] ||
                workspaceFeature?.user_stories ||
                workspaceFeature?.userStories ||
                workspaceFeature?.stories ||
                [];
            const hierarchySourceFeatures = completeProductHierarchy || hierarchyFeatures;
            const hierarchyProject = {
                ...validatedProject,
                features: hierarchySourceFeatures.map(candidate => {
                    const candidateId = Number(candidate?.featureid || candidate?.id || 0);
                    const loadedFeature = hierarchyFeatures.find(item => Number(item?.featureid || item?.id || 0) === candidateId);
                    const cachedStories =
                        hierarchyStoriesByFeature[candidateId] || loadedFeature?.user_stories || loadedFeature?.userStories || loadedFeature?.stories || [];
                    const hierarchyStories = Array.isArray(candidate?.user_stories) ? candidate.user_stories : cachedStories;
                    return {
                        ...loadedFeature,
                        ...candidate,
                        id: candidateId,
                        name: candidate?.name || candidate?.title || loadedFeature?.name || loadedFeature?.title,
                        user_stories: hierarchyStories.map((candidateStory: any) => {
                            const storyId = String(candidateStory?.user_story_id || candidateStory?.id || "");
                            const loadedStory = cachedStories.find((item: any) => String(item?.user_story_id || item?.id || "") === storyId);
                            return {
                                ...loadedStory,
                                ...candidateStory,
                                id: storyId,
                                title: candidateStory?.title || loadedStory?.title || loadedStory?.name
                            };
                        })
                    };
                })
            };
            return (
                <SddWorkspace
                    // Keep the workspace mounted while changing Feature/User Story scope so
                    // the Optional Work Products dropdowns do not reset the scroll position.
                    key={validatedProject?.project_id || validatedProject?.id || "project"}
                    project={hierarchyProject}
                    feature={workspaceFeature ? { ...workspaceFeature, user_stories: workspaceStories } : null}
                    story={selectedUserStory}
                    workProductsByPhase={workProductsByPhase}
                    requiredWorkProductsByPhase={requiredWorkProducts || undefined}
                    onGenerate={handleRequiredGenerate}
                    onOpen={handleCardOpen}
                    onApprove={handleSddApproval}
                    onReviewsChanged={loadSddProgress}
                    getWorkProductState={getRequiredWorkProductState}
                    onFeatureSelect={props.onFeatureSelect}
                    onStorySelect={props.onUserStorySelect}
                    optionalFeature={optionalFeatureWithStories}
                    optionalStory={optionalStoryScope}
                    optionalWorkProductsByPhase={optionalWorkProducts || {}}
                    optionalWorkProductsLoading={optionalWorkProductsLoading}
                    onOptionalFeatureSelect={handleOptionalFeatureSelect}
                    onOptionalStorySelect={handleOptionalStorySelect}
                    onGenerateOptional={handleOptionalGenerate}
                    getOptionalWorkProductState={getOptionalWorkProductState}
                    progress={sddProgress}
                    progressLoading={sddProgressLoading}
                    loadProgressReport={loadSddProgressReport}
                    exportProgressReport={downloadSddProgressReport}
                    onAddFeature={props.onAddFeature}
                    onAddStory={props.onAddUserStory}
                    onCreateFromHierarchy={props.onCreateFromHierarchy}
                    onEditFeature={props.onEditFeature}
                    onEditStory={props.onEditUserStory}
                    onHierarchyPageOpen={() => void loadCompleteProductHierarchy()}
                    hierarchyPageOpen={sddHierarchyPageOpen}
                    onHierarchyPageOpenChange={onSddHierarchyPageOpenChange}
                    scopePickerPageOpen={sddScopePickerOpen}
                    onScopePickerOpenChange={onSddScopePickerOpenChange}
                    progressReportPageOpen={sddProgressReportOpen}
                    onProgressReportOpenChange={onSddProgressReportOpenChange}
                    contextMesh={
                        props.onOpenMeshStrength ? (
                            <MeshStrengthEntryCard
                                projectId={validatedProject?.project_id ?? validatedProject?.id ?? null}
                                onOpen={props.onOpenMeshStrength}
                                variant="monitor"
                            />
                        ) : null
                    }
                />
            );
        }
        case "code":
        case "discovery": {
            // Shared work-product card rendering for Discovery and Code phases.
            const phaseWorkProducts = workProductsByPhase[featureStudioTab] || [];
            const activeWorkProducts = phaseWorkProducts
                .filter(wp => wp.state === "editable" || wp.state === "available-to-add")
                .filter(wp => !wp.name.includes("(HIDDEN)"));
            const sortedWorkProducts = [...activeWorkProducts].sort((a, b) => {
                const aIsGenerated = a.hasContent || a.state === "editable";
                const bIsGenerated = b.hasContent || b.state === "editable";
                if (aIsGenerated && !bIsGenerated) return -1;
                if (!aIsGenerated && bIsGenerated) return 1;
                return 0;
            });

            // Empty state check
            if (!isLoadingWorkProducts && sortedWorkProducts.length === 0) {
                return (
                    <MuiBox sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
                        <MuiBox sx={{ textAlign: "center", maxWidth: 400 }}>
                            <MuiTypography variant="h6" sx={{ mb: 1, color: "text.secondary" }}>
                                No Work Products Available
                            </MuiTypography>
                            <MuiTypography variant="body2" sx={{ color: "text.disabled" }}>
                                There are no work products configured for this selection level yet.
                            </MuiTypography>
                        </MuiBox>
                    </MuiBox>
                );
            }

            return (
                <MuiBox sx={{ height: "100%", overflow: "auto", p: 2 }}>
                    <MuiBox sx={{ width: "100%" }}>
                        {isLoadingWorkProducts ? (
                            <MuiBox sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
                                <MuiBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <MuiCircularProgress size={16} />
                                    <MuiTypography variant="body2" color="text.secondary">
                                        Loading...
                                    </MuiTypography>
                                </MuiBox>
                            </MuiBox>
                        ) : (
                            <MuiBox
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 2,
                                    p: 0.5,
                                    minHeight: "100%",
                                    boxSizing: "border-box"
                                }}
                            >
                                {/* Work Products Header with View Switcher */}
                                <MuiBox sx={{ display: "flex", alignItems: "center", gap: 1, padding: "0 28px 14px" }}>
                                    <MuiTypography
                                        sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "grey.400" }}
                                    >
                                        Work Products
                                    </MuiTypography>
                                    <MuiBox
                                        sx={{
                                            fontFamily: "monospace",
                                            fontSize: 11,
                                            color: "grey.400",
                                            padding: "2px 7px",
                                            borderRadius: "999px",
                                            backgroundColor: "rgba(0, 0, 0, 0.04)"
                                        }}
                                    >
                                        {sortedWorkProducts.length}
                                    </MuiBox>
                                    <MuiBox sx={{ flex: 1 }} />

                                    {/* View Switcher */}
                                    <MuiBox
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            padding: "4px",
                                            borderRadius: "6px",
                                            backgroundColor: "rgba(0, 0, 0, 0.04)"
                                        }}
                                    >
                                        <MuiIconButton
                                            size="small"
                                            onClick={() => onViewModeChange("grid")}
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                backgroundColor: viewMode === "grid" ? "background.paper" : "transparent",
                                                boxShadow: viewMode === "grid" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                                                "&:hover": { backgroundColor: viewMode === "grid" ? "background.paper" : "rgba(0, 0, 0, 0.02)" }
                                            }}
                                        >
                                            <GridViewIcon sx={{ fontSize: 16 }} />
                                        </MuiIconButton>
                                        <MuiIconButton
                                            size="small"
                                            onClick={() => onViewModeChange("list")}
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                backgroundColor: viewMode === "list" ? "background.paper" : "transparent",
                                                boxShadow: viewMode === "list" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                                                "&:hover": { backgroundColor: viewMode === "list" ? "background.paper" : "rgba(0, 0, 0, 0.02)" }
                                            }}
                                        >
                                            <ViewListIcon sx={{ fontSize: 16 }} />
                                        </MuiIconButton>
                                    </MuiBox>
                                </MuiBox>

                                <MuiBox
                                    sx={{
                                        display: viewMode === "grid" ? "grid" : "flex",
                                        flexDirection: viewMode === "list" ? "column" : undefined,
                                        gridTemplateColumns:
                                            viewMode === "grid"
                                                ? {
                                                      xs: "1fr",
                                                      sm: "repeat(2, 1fr)",
                                                      md: "repeat(3, 1fr)",
                                                      lg: "repeat(4, 1fr)",
                                                      xl: "repeat(5, 1fr)"
                                                  }
                                                : undefined,
                                        gap: 2,
                                        width: "100%",
                                        flex: 1,
                                        alignContent: "start"
                                    }}
                                >
                                    {sortedWorkProducts.map(wp => {
                                        // Handle both 'id' and 'featureid' properties for feature identification
                                        const featureIdForKey = selectedFeature?.id || (selectedFeature as any)?.featureid;
                                        const wpKey = generateWorkProductKey(wp.id, validatedProject?.project_id, featureIdForKey, selectedUserStory?.id);
                                        const wpState = getWorkProductState(selectedNodeKey, wp.id);

                                        // Build context for UI bridge: product/feature-{id}/userstory-{id}
                                        let context = "product";
                                        if (selectedUserStory?.id) {
                                            context = `userstory-${selectedUserStory.id}`;
                                        } else if (featureIdForKey) {
                                            context = `feature-${featureIdForKey}`;
                                        }

                                        // Sync scope needs the FEATURE id, not wp.id (which is a work-product id).
                                        const numericFeatureId = featureIdForKey != null ? Number(featureIdForKey) : NaN;

                                        return (
                                            <FeatureStudioCard
                                                key={wp.id}
                                                workProduct={wp}
                                                state={wpState}
                                                onGenerate={() => handleCardGenerate(wp)}
                                                onOpen={() => handleCardOpen(wp)}
                                                viewMode={viewMode}
                                                context={context}
                                                phase={featureStudioTab}
                                                onSyncClick={!isNaN(numericFeatureId) ? () => onSyncClick(numericFeatureId) : undefined}
                                            />
                                        );
                                    })}
                                </MuiBox>
                            </MuiBox>
                        )}
                    </MuiBox>
                </MuiBox>
            );
        }
        // TEMPORARY: Empty state for SDLC tabs (Design, QA, Deploy)
        // Remove this block when Work Products are implemented for these phases
        case "design":
        case "qa":
        case "deploy":
            // Show Coming Soon message for these tabs
            return (
                <MuiBox
                    sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 360,
                        bgcolor: "background.default"
                    }}
                >
                    <MuiTypography
                        variant="h5"
                        sx={{
                            fontStyle: "italic",
                            fontWeight: 400,
                            color: "text.secondary",
                            textAlign: "center"
                        }}
                    >
                        Work Products coming soon.
                    </MuiTypography>
                </MuiBox>
            );
        case "analytics":
            return (
                <MuiBox sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
                    <MuiPaper
                        elevation={0}
                        sx={{ p: 6, textAlign: "center", opacity: 0.9, bgcolor: "background.paper", borderRadius: 2, border: 1, borderColor: "divider" }}
                    >
                        <AnalyticsIcon sx={{ fontSize: 64, color: "text.disabled", mb: 3 }} />
                        <MuiTypography variant="h5" sx={{ color: "text.secondary", fontWeight: 500, mb: 2 }}>
                            Analytics Dashboard
                        </MuiTypography>
                        <MuiTypography variant="body1" sx={{ color: "text.disabled", mb: 4, maxWidth: 400 }}>
                            Advanced analytics for burndown charts, velocity tracking, and performance insights.
                        </MuiTypography>
                        <MuiBox sx={{ width: 200, mx: "auto" }}>
                            <MuiTypography variant="caption" sx={{ color: "text.disabled", mb: 1, display: "block" }}>
                                Development in progress
                            </MuiTypography>
                            <MuiLinearProgress
                                variant="determinate"
                                value={65}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: "action.hover",
                                    "& .MuiLinearProgress-bar": { borderRadius: 3, bgcolor: "primary.main" }
                                }}
                            />
                        </MuiBox>
                    </MuiPaper>
                </MuiBox>
            );
        case "history":
            return (
                <MuiBox sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
                    <MuiCard elevation={0} sx={{ p: 6, textAlign: "center", maxWidth: 500, border: 1, borderColor: "divider" }}>
                        <MuiCardContent sx={{ p: 0 }}>
                            <HistoryIcon sx={{ fontSize: 64, color: "text.disabled", mb: 3 }} />
                            <MuiTypography variant="h5" sx={{ color: "text.secondary", fontWeight: 500, mb: 2 }}>
                                Version History
                            </MuiTypography>
                            <MuiTypography variant="body1" sx={{ color: "text.disabled", mb: 4 }}>
                                Track changes, rollback capabilities, and collaborative editing history for all work products.
                            </MuiTypography>
                            <MuiBox sx={{ mb: 3 }}>
                                <MuiTypography variant="caption" sx={{ color: "text.disabled", mb: 1, display: "block" }}>
                                    Feature implementation
                                </MuiTypography>
                                <MuiLinearProgress
                                    variant="indeterminate"
                                    sx={{
                                        height: 4,
                                        borderRadius: 2,
                                        bgcolor: "action.hover",
                                        "& .MuiLinearProgress-bar": { borderRadius: 2, bgcolor: "secondary.main" }
                                    }}
                                />
                            </MuiBox>
                        </MuiCardContent>
                    </MuiCard>
                </MuiBox>
            );
        default:
            return null;
    }
};

const MemoizedTabContent = React.memo(TabContent);
MemoizedTabContent.displayName = "TabContent";

export default function FeatureStudioView(props: FeatureStudioViewProps) {
    const {
        selectedProject,
        initiatives,
        selectedFeature,
        isHierarchyCollapsed,
        featureStudioTab,
        selectedUserStory,
        hierarchyExpandedItems,
        onHierarchyExpandedItemsChange,
        onBackToProjects,
        onAddFeature,
        onAddUserStory,
        onHierarchyToggle,
        onFeatureSelect,
        onUserStorySelect,
        onFeaturePromote,
        featuresRefreshTrigger,
        workProductRefreshTrigger,
        workProductRefreshScope,
        onEditFeature,
        onDeleteFeature,
        onEditUserStory,
        onDeleteUserStory,
        deletingFeatureId,
        onFeatureDeleteComplete,
        onNavigateToIntegrations,
        onOpenMeshStrength
    } = props;

    const [showPromotionModal, setShowPromotionModal] = React.useState(false);
    const [viewMode, setViewMode] = React.useState<"list" | "grid">("grid");
    const [readinessIssues, setReadinessIssues] = React.useState<string[]>([]);
    const [showReadinessDrawer, setShowReadinessDrawer] = React.useState(false);
    const [batchPending, setBatchPending] = React.useState(false);
    const [batchError, setBatchError] = React.useState<string | null>(null);
    const [sddHierarchyPageOpen, setSddHierarchyPageOpen] = React.useState(false);
    const [sddScopePickerOpen, setSddScopePickerOpen] = React.useState(false);
    const [sddProgressReportOpen, setSddProgressReportOpen] = React.useState(false);
    const sddSubpageOpen = featureStudioTab === "sdd" && (sddHierarchyPageOpen || sddScopePickerOpen || sddProgressReportOpen);

    React.useEffect(() => {
        const handleSddNavigation = (event: Event) => {
            const name = (event as CustomEvent<{ name?: string }>).detail?.name;
            setSddHierarchyPageOpen(name === "navigate_to_sdd_product_hierarchy");
            setSddScopePickerOpen(name === "navigate_to_sdd_scope_picker");
            setSddProgressReportOpen(name === "navigate_to_sdd_progress_report");
        };
        window.addEventListener("fde:sdd-navigation", handleSddNavigation);
        return () => window.removeEventListener("fde:sdd-navigation", handleSddNavigation);
    }, []);

    React.useEffect(() => {
        const handleSddScopeSelection = (event: Event) => {
            const kind = (event as CustomEvent<{ kind?: string }>).detail?.kind;
            if (kind === "story") setSddScopePickerOpen(false);
        };
        window.addEventListener("fde:sdd-scope-selection", handleSddScopeSelection);
        return () => window.removeEventListener("fde:sdd-scope-selection", handleSddScopeSelection);
    }, []);

    const handleFeatureStudioBack = React.useCallback(() => {
        if (sddHierarchyPageOpen) {
            setSddHierarchyPageOpen(false);
            return;
        }
        if (sddScopePickerOpen) {
            setSddScopePickerOpen(false);
            return;
        }
        if (sddProgressReportOpen) {
            setSddProgressReportOpen(false);
            return;
        }
        onBackToProjects();
    }, [onBackToProjects, sddHierarchyPageOpen, sddProgressReportOpen, sddScopePickerOpen]);

    // Sync panel (Jira/Azure DevOps) — scoped to a feature id resolved by the
    // caller (FeatureStudioCard's workProduct.id is a work-product id, not a
    // feature id; see the onSyncClick wiring in TabContent above).
    const [syncFeatureId, setSyncFeatureId] = React.useState<number | null>(null);

    // Tree selection and expansion state
    const [selectedNodeKey, setSelectedNodeKey] = React.useState<string>("");
    const [pendingTreeSelectionKey, setPendingTreeSelectionKey] = React.useState<string | null>(null);
    const [expandedNodes, setExpandedNodes] = React.useState<Set<string>>(() => new Set(hierarchyExpandedItems || []));

    // Work product view mode (grid/list)
    const [workProductViewMode, setWorkProductViewMode] = React.useState<"grid" | "list">("grid");

    // Resizable panel state
    const [leftPanelWidth, setLeftPanelWidth] = React.useState(280); // Default width in pixels
    const [isResizing, setIsResizing] = React.useState(false);
    const resizeStartX = React.useRef(0);
    const resizeStartWidth = React.useRef(280);
    const MIN_PANEL_WIDTH = 200;
    const MAX_PANEL_WIDTH = 600;

    // State machine layer: local overrides on top of Redux state
    const [stateOverrides, setStateOverrides] = React.useState<Record<string, Record<string, WorkProductState>>>({});

    // Consolidated state management to reduce re-renders
    const [loadingState, setLoadingState] = useState({
        isInitializing: true,
        isLoadingProject: false,
        isLoadingFeatures: false,
        isLoadingFeature: false
    });

    const [dataState, setDataState] = useState({
        features: [] as Feature[],
        currentFeature: null as Feature | null,
        validatedProject: null as any,
        projectWithFeatures: null as any
    });

    // Refs to prevent duplicate loads and track initialization
    const hasInitialized = React.useRef(false);
    const lastLoadedProjectId = React.useRef<number | null>(null);
    const lastLoadedFeatureId = React.useRef<number | null>(null);
    const lastFetchParams = React.useRef<{ featureId: number | null; userStoryId: string | null }>({ featureId: null, userStoryId: null });

    const token = useAppSelector(state => state.clientToken.token);
    const userStoriesByFeature = useAppSelector(state => state.userStories.userStoriesByFeature);
    const dispatch = useAppDispatch();
    const batchGeneration = useOptionalBatchGeneration();
    const { jobs: backgroundJobs, batches: backgroundBatches, openForJob: openBackgroundActivity } = useBackgroundActivity();

    const batchProductId = Number(dataState.validatedProject?.project_id);
    const activeProductBatch = React.useMemo(() => {
        let current: TrackedBatch | undefined;
        for (const batch of backgroundBatches) {
            if (!batch.handle?.targets.some(target => target.productId === batchProductId)) continue;
            const candidateActive = isActiveProductBatch(batch);
            const currentActive = isActiveProductBatch(current);
            const candidateTime = Date.parse(batch.handle.updatedAt || batch.createdAt) || 0;
            const currentTime = current ? Date.parse(current.handle?.updatedAt || current.createdAt) || 0 : 0;
            if (!current || (candidateActive && !currentActive) || (candidateActive === currentActive && candidateTime >= currentTime)) {
                current = batch;
            }
        }
        return current;
    }, [backgroundBatches, batchProductId]);

    const batchIsActive = isActiveProductBatch(activeProductBatch);
    const batchStatus: "idle" | "submitting" | "running" | "complete" | "partial" = batchPending
        ? "submitting"
        : activeProductBatch?.phase === "completed"
          ? "complete"
          : activeProductBatch && ["partial_failure", "failed", "cancelled"].includes(activeProductBatch.phase)
            ? "partial"
            : batchIsActive
              ? "running"
              : "idle";

    // Keep the middle-panel scope picker self-sufficient. A user can select a
    // feature from a Work Product card without ever expanding the left tree,
    // so the tree cannot be the only place that hydrates that feature's stories.
    React.useEffect(() => {
        const featureId = Number(selectedFeature?.id || selectedFeature?.featureid);
        if (!featureId || !token || featureId in userStoriesByFeature) return;
        void dispatch(fetchFeatureUserStories({ featureId, idToken: token }));
    }, [dispatch, selectedFeature, token, userStoriesByFeature]);

    const generateAllWorkProducts = React.useCallback(async () => {
        if (!batchGeneration || !Number.isInteger(batchProductId) || batchProductId <= 0 || batchPending || batchIsActive) return;

        const showReadiness = (readiness: BatchReadiness) => {
            const issues = readiness.products.flatMap(product => product.missingInputs);
            setReadinessIssues(issues.length > 0 ? issues : ["Complete the required Context Mesh inputs before generating Work Products."]);
            setShowReadinessDrawer(true);
        };

        setBatchPending(true);
        setBatchError(null);
        try {
            const readiness = await batchGeneration.checkReadiness([batchProductId], { trackChat: false });
            if (!readiness.ready) {
                showReadiness(readiness);
                return;
            }
            await batchGeneration.startBatch({
                productIds: [batchProductId],
                origin: "feature-studio-ui"
            });
        } catch (error) {
            const readiness = (error as Error & { readiness?: BatchReadiness }).readiness;
            if (readiness && !readiness.ready) showReadiness(readiness);
            else setBatchError(error instanceof Error ? error.message : "Unable to start batch generation.");
        } finally {
            setBatchPending(false);
        }
    }, [batchGeneration, batchIsActive, batchPending, batchProductId]);

    // Live generation detail comes from the processing-jobs feed that also backs
    // the Background Activity flyout. The card's own job poll only carries queue
    // lifecycle (no percent/section), so without this the card would sit on its
    // seeded "Starting generation..." message for the whole run.
    const liveProgressByWorkProductId = React.useMemo(
        () => selectLiveWorkProductProgress(backgroundJobs, dataState.validatedProject?.project_id),
        [backgroundJobs, dataState.validatedProject?.project_id]
    );

    /**
     * Get work product state by merging Redux data with local overrides
     */
    const getWorkProductState = React.useCallback(
        (nodeKey: string, wpId: string): WorkProductState => {
            // Derive from Redux state
            const currentPhase = featureStudioTab;
            const wp = props.workProductsByPhase[currentPhase]?.find((w: any) => w.id === wpId);

            // IMPORTANT: Check if generating FIRST, before checking hasContent
            // This prevents premature "ready" state when backend is still generating sections
            const projectId = dataState.validatedProject?.project_id;
            const featureId = selectedFeature?.id || selectedFeature?.featureid;
            const userStoryId = selectedUserStory?.id;
            const wpKey = generateWorkProductKey(wpId, projectId, featureId, userStoryId);

            // Priority 1: Check if actively generating (via Redux tracking)
            if (props.generatingWorkProducts.has(wpKey)) {
                const progress = props.workProductProgress?.get(wpKey);
                // Prefer the live processor detail ("Processing section 4 of 6", 50%)
                // over the locally seeded message, which never advances.
                const liveJob = liveProgressByWorkProductId.get(String(wpId));
                return {
                    state: "generating",
                    step: liveJob?.message || progress?.step || progress?.message || "Processing...",
                    progress: liveJob?.progress ?? null
                };
            }

            // Priority 2: Check local optimistic override
            const override = stateOverrides[nodeKey]?.[wpId];
            if (override) return override;

            // Priority 3: Check if ready (only after confirming NOT generating)
            if (wp?.hasContent || wp?.state === "editable") {
                return {
                    state: "ready",
                    updated: wp.updatedAt || "Today",
                    preview: wp.preview || `${wp.name} has been generated. Click to open and refine.`,
                    artifactId: wp.id
                };
            }

            // Default to idle
            return { state: "idle" };
        },
        [
            stateOverrides,
            featureStudioTab,
            props.workProductsByPhase,
            props.generatingWorkProducts,
            props.workProductProgress,
            liveProgressByWorkProductId,
            dataState.validatedProject,
            selectedFeature,
            selectedUserStory
        ]
    );

    /**
     * Handle work product generation (idle → generating)
     */
    const handleCardGenerate = React.useCallback(
        (workProduct: any, scope?: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null }) => {
            const projectIdOverride = Number(dataState.validatedProject?.project_id || dataState.validatedProject?.id || 0) || undefined;
            // Optimistically set generating state
            setStateOverrides(prev => ({
                ...prev,
                [selectedNodeKey]: {
                    ...prev[selectedNodeKey],
                    [workProduct.id]: { state: "generating", step: "Starting..." }
                }
            }));

            // Call existing handler which triggers backend
            return props.handleWorkProductGenerate(workProduct, projectIdOverride, scope ? { scope } : undefined);
        },
        [dataState.validatedProject, selectedNodeKey, props.handleWorkProductGenerate]
    );

    /**
     * Handle work product open (ready → editor)
     */
    const handleCardOpen = React.useCallback(
        (workProduct: any, scope?: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null }) => {
            props.handleWorkProductSelect(workProduct, scope);
        },
        [props.handleWorkProductSelect]
    );

    // Calculate header and stats data based on current selection
    const selectionData = React.useMemo(() => {
        if (!dataState.validatedProject) return null;

        if (selectedUserStory && dataState.currentFeature) {
            const storyTitle = formatStoryHeaderTitle(selectedUserStory);
            const storySubtitle = `As a ${selectedUserStory.asA || "user"}, I want to ${selectedUserStory.want || selectedUserStory.description} so that ${selectedUserStory.soThat || "value is delivered"}.`;

            return {
                kind: "User Story" as const,
                title: storyTitle,
                subtitle: storySubtitle,
                level: "story" as const
            };
        }

        if (dataState.currentFeature) {
            return {
                kind: "Feature" as const,
                title: dataState.currentFeature.title || dataState.currentFeature.name,
                subtitle: dataState.currentFeature.description || "No description available",
                level: "feature" as const
            };
        }

        // Product level
        return {
            kind: "Product" as const,
            title: dataState.validatedProject.project_name || dataState.validatedProject.name,
            subtitle: dataState.validatedProject.description || dataState.validatedProject.project_description || "No description available",
            level: "product" as const
        };
    }, [selectedUserStory, dataState.currentFeature, dataState.validatedProject]);

    const stats = React.useMemo(() => {
        if (!selectionData || !dataState.validatedProject) return null;

        return calculateStats(
            selectionData.level,
            dataState.validatedProject,
            dataState.currentFeature,
            selectedUserStory,
            props.workProductsByPhase,
            props.generatingWorkProducts
        );
    }, [selectionData, dataState.validatedProject, dataState.currentFeature, selectedUserStory, props.workProductsByPhase, props.generatingWorkProducts]);

    // Wrap the handleAddWorkProductToEditable to pass the correct projectId
    const wrappedHandleAddWorkProductToEditable = React.useCallback(
        (workProduct: any) => {
            const projectId = dataState.validatedProject?.project_id;
            console.log("FeatureStudioView: Calling handler with projectId:", projectId);
            // Call the original handler with the workProduct and pass projectId as the second parameter
            props.handleAddWorkProductToEditable(workProduct, projectId);
        },
        [props.handleAddWorkProductToEditable, dataState.validatedProject]
    );

    // Handle project selection - clears feature which triggers useEffect to fetch PRODUCT level
    const handleProjectSelect = React.useCallback((project: any) => {
        console.log("Project selected:", project.project_name);
        // Note: The useEffect watching selectedFeature will automatically fetch PRODUCT level
        // work products when feature is cleared (set to null by WorkProductHierarchy)
        // So we don't need to do anything here - just a placeholder for future project-specific logic
    }, []);

    const applyExpandedNodes = React.useCallback(
        (updater: (prev: Set<string>) => Set<string>) => {
            setExpandedNodes(prev => {
                const next = updater(prev);
                onHierarchyExpandedItemsChange(Array.from(next));
                return next;
            });
        },
        [onHierarchyExpandedItemsChange]
    );

    const handleTreeSelect = React.useCallback(
        (key: string) => {
            if (!key || key === selectedNodeKey) return;
            setSelectedNodeKey(key);

            // Selection cascade: auto-expand
            applyExpandedNodes(prev => {
                const next = new Set(prev);

                // Always keep product expanded
                const productKey = `P:${dataState.validatedProject?.project_id}`;
                next.add(productKey);

                // If selecting a Feature, expand it
                if (key.startsWith("F:")) {
                    next.add(key);
                }

                // If selecting a Story, expand its parent Feature
                if (key.startsWith("S:")) {
                    const featureId = key.split(":")[1];
                    next.add(`F:${featureId}`);
                }

                return next;
            });
            // Defer parent-owned selection updates to an effect phase.
            // This avoids "Cannot update App while rendering FeatureStudioView"
            // if a child invokes onSelect during render.
            setPendingTreeSelectionKey(key);
        },
        [applyExpandedNodes, dataState.validatedProject, selectedNodeKey]
    );

    React.useEffect(() => {
        if (!pendingTreeSelectionKey) return;

        const key = pendingTreeSelectionKey;
        setPendingTreeSelectionKey(null);

        if (key.startsWith("P:")) {
            onFeatureSelect(null);
            onUserStorySelect?.(null);
            return;
        }

        if (key.startsWith("F:")) {
            const featureId = key.slice(2);
            const feature = dataState.features.find(f => String(f.featureid || f.id) === featureId);
            if (feature) {
                onFeatureSelect(feature);
                onUserStorySelect?.(null);
            }
            return;
        }

        if (key.startsWith("S:")) {
            const [, featureId, storyId] = key.split(":");
            const userStories = userStoriesByFeature[Number(featureId)];
            const userStory = userStories?.find((us: any) => String(us.id) === storyId);
            if (!userStory || !onUserStorySelect) return;

            const feature = dataState.features.find(f => String(f.featureid || f.id) === featureId);
            if (feature) {
                onFeatureSelect(feature);
            }
            onUserStorySelect(userStory);
        }
    }, [pendingTreeSelectionKey, dataState.features, userStoriesByFeature, onFeatureSelect, onUserStorySelect]);

    const handleTreeToggleExpand = React.useCallback(
        (key: string) => {
            applyExpandedNodes(prev => {
                const next = new Set(prev);
                if (next.has(key)) {
                    next.delete(key);
                } else {
                    next.add(key);
                }
                return next;
            });
        },
        [applyExpandedNodes]
    );

    // Resize handlers for left panel
    const handleMouseDown = React.useCallback(
        (e: React.MouseEvent) => {
            resizeStartX.current = e.clientX;
            resizeStartWidth.current = leftPanelWidth;
            setIsResizing(true);
        },
        [leftPanelWidth]
    );

    const handleMouseMove = React.useCallback(
        (e: MouseEvent) => {
            if (!isResizing) return;
            const delta = e.clientX - resizeStartX.current;
            const newWidth = resizeStartWidth.current + delta;
            if (newWidth >= MIN_PANEL_WIDTH && newWidth <= MAX_PANEL_WIDTH) {
                setLeftPanelWidth(newWidth);
            }
        },
        [isResizing]
    );

    const handleMouseUp = React.useCallback(() => {
        setIsResizing(false);
    }, []);

    // Add/remove mouse event listeners for resizing
    React.useEffect(() => {
        if (isResizing) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
        } else {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        }
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    // Clear state overrides when work products complete generation
    React.useEffect(() => {
        // Iterate through all phases and check if any work products are now editable
        Object.keys(props.workProductsByPhase).forEach(phase => {
            const workProducts = props.workProductsByPhase[phase] || [];
            workProducts.forEach(wp => {
                // If work product is editable in Redux but has a generating override, clear it
                if ((wp.state === "editable" || wp.hasContent) && stateOverrides[selectedNodeKey]?.[wp.id]?.state === "generating") {
                    setStateOverrides(prev => {
                        const newOverrides = { ...prev };
                        if (newOverrides[selectedNodeKey]) {
                            const { [wp.id]: removed, ...rest } = newOverrides[selectedNodeKey];
                            if (Object.keys(rest).length === 0) {
                                delete newOverrides[selectedNodeKey];
                            } else {
                                newOverrides[selectedNodeKey] = rest;
                            }
                        }
                        return newOverrides;
                    });
                }
            });
        });
    }, [props.workProductsByPhase, selectedNodeKey, stateOverrides]);

    // Get current project from Redux state (removed unused variables)

    // Use the prop instead of Redux state - the prop is being updated by the handlers
    // const workProductsByPhase = useAppSelector(state => state.workProductByPhase?.workProductsByPhase || {});
    const workProductsLoadingStatus = useAppSelector(state => (state as any).workProductByPhase?.status || "idle");

    // Consolidated initialization effect - load everything in one coordinated flow
    useEffect(() => {
        if (!token || hasInitialized.current) return;

        const initializeAllData = async () => {
            hasInitialized.current = true;

            // Set loading state at the beginning
            setLoadingState(prev => ({ ...prev, isLoadingProject: true }));

            try {
                // Load projects
                const projectsResult = await dispatch(fetchProjects()).unwrap();

                // An explicit navigation selection is newer than the persisted
                // `current` flag returned by the project list. Prefer the fresh
                // API record for that selected id so a previous product cannot
                // replace the FDE/manual navigation target during initialization.
                const selectedProjectId = Number(selectedProject?.project_id ?? selectedProject?.id);
                const explicitlySelectedProject =
                    Number.isSafeInteger(selectedProjectId) && selectedProjectId > 0
                        ? (projectsResult.find(p => Number(p.project_id) === selectedProjectId) ?? selectedProject)
                        : selectedProject;
                const currentProject = explicitlySelectedProject || projectsResult.find(p => p.current === true) || projectsResult[0] || null;

                if (!currentProject) {
                    setLoadingState({ isInitializing: false, isLoadingProject: false, isLoadingFeatures: false, isLoadingFeature: false });
                    setDataState(prev => ({ ...prev, validatedProject: null }));
                    return;
                }

                const projectId = currentProject.project_id;
                lastLoadedProjectId.current = projectId;

                // Load features for the project
                const fetchedFeatures = await fetchProjectFeaturesApi(projectId, token);

                // Transform features
                const transformedFeatures = fetchedFeatures.map((f: Feature) => ({
                    ...f,
                    id: f.featureid,
                    name: f.title
                }));

                // Don't auto-select any feature - show Product/Project level by default
                let selectedFeatureData = null;

                // Load work products at PRODUCT level by default
                dispatch(
                    fetchWorkProductsByPhase({
                        projectId: projectId,
                        featureId: undefined,
                        userStoryId: undefined,
                        level: "PRODUCT"
                    })
                );

                // Update all state in one batch
                setDataState({
                    validatedProject: currentProject,
                    features: fetchedFeatures,
                    currentFeature: selectedFeatureData,
                    projectWithFeatures: {
                        ...currentProject,
                        features: transformedFeatures
                    }
                });
            } catch (error) {
                console.error("Error initializing data:", error);
                setDataState(prev => ({
                    ...prev,
                    validatedProject: selectedProject || null
                }));
            } finally {
                setLoadingState({
                    isInitializing: false,
                    isLoadingProject: false,
                    isLoadingFeatures: false,
                    isLoadingFeature: false
                });
            }
        };

        initializeAllData();
    }, [token, dispatch, onFeatureSelect]); // Minimal dependencies

    // Track last loaded project and user story to avoid unnecessary fetches
    const lastLoadedProjectIdRef = useRef<number | null>(null);
    const lastLoadedUserStoryIdRef = useRef<string | null>(null);

    // Handle feature selection changes (from hierarchy or other sources)
    useEffect(() => {
        // Handle when feature is cleared (project-level view)
        if (!selectedFeature && dataState.validatedProject && !loadingState.isInitializing && token) {
            const projectId = dataState.validatedProject.project_id;
            const numericProjectId = typeof projectId === "string" ? parseInt(projectId, 10) : projectId;

            // Always fetch when navigating back to product level, even if it's the same project
            // This ensures fresh data after viewing feature/user story level
            if (numericProjectId) {
                console.log("Feature cleared - fetching PRODUCT level work products");
                lastLoadedFeatureId.current = null;
                lastLoadedProjectIdRef.current = numericProjectId;
                lastFetchParams.current = { featureId: null, userStoryId: null }; // Clear fetch params
                setDataState(prev => ({ ...prev, currentFeature: null }));

                // Fetch work products at PRODUCT level
                dispatch(
                    fetchWorkProductsByPhase({
                        projectId: numericProjectId,
                        featureId: undefined,
                        userStoryId: undefined,
                        level: "PRODUCT"
                    })
                );
            }
            return;
        }

        // Handle when feature is selected
        if (!selectedFeature || !dataState.validatedProject || loadingState.isInitializing) return;

        // Handle both featureid (API type) and id (transformed type)
        const featureId = (selectedFeature as any).id || selectedFeature.featureid;
        const userStoryId = selectedUserStory?.id;

        console.log("Feature selection changed:", {
            featureId,
            userStoryId,
            selectedFeature,
            lastLoadedFeatureId: lastLoadedFeatureId.current,
            lastLoadedUserStoryId: lastLoadedUserStoryIdRef.current
        });

        // Avoid reloading if it's the same feature AND same user story
        if (lastLoadedFeatureId.current === featureId && lastLoadedUserStoryIdRef.current === userStoryId) {
            console.log("Skipping fetch - same feature and user story");
            return;
        }

        lastLoadedFeatureId.current = featureId;
        lastLoadedUserStoryIdRef.current = userStoryId || null;

        // Update current feature using functional state update to avoid stale closure
        setDataState(prev => {
            const feature = findFeatureBySelectedId(prev.features, selectedFeature);
            return { ...prev, currentFeature: feature || null };
        });

        // Load work products for the selected feature
        const projectId = dataState.validatedProject.project_id;
        const numericProjectId = typeof projectId === "string" ? parseInt(projectId, 10) : projectId;
        const numericFeatureId = typeof featureId === "string" ? parseInt(featureId, 10) : featureId;

        if (numericProjectId && numericFeatureId && token) {
            // Determine level based on context
            const level = selectedUserStory?.id ? "USERSTORY" : "FEATURE";
            console.log("Fetching work products:", {
                projectId: numericProjectId,
                featureId: numericFeatureId,
                userStoryId: selectedUserStory?.id,
                level
            });
            dispatch(
                fetchWorkProductsByPhase({
                    projectId: numericProjectId,
                    featureId: numericFeatureId,
                    userStoryId: selectedUserStory?.id,
                    level: level
                })
            );
        }
    }, [selectedFeature, selectedUserStory, dataState.validatedProject, loadingState.isInitializing, token, dispatch]);

    // Handle features refresh trigger
    useEffect(() => {
        if (featuresRefreshTrigger && dataState.validatedProject && token && !loadingState.isInitializing) {
            const projectId = dataState.validatedProject.project_id;

            setLoadingState(prev => ({ ...prev, isLoadingFeatures: true }));

            fetchProjectFeaturesApi(projectId, token)
                .then((fetchedFeatures: Feature[]) => {
                    // Filter out the feature being deleted to prevent race condition
                    // where backend hasn't fully processed the deletion yet
                    const filteredFeatures = deletingFeatureId
                        ? fetchedFeatures.filter((f: Feature) => {
                              const fId = f.featureid || (f as any).id;
                              return fId?.toString() !== deletingFeatureId?.toString();
                          })
                        : fetchedFeatures;

                    const transformedFeatures = filteredFeatures.map((f: Feature) => ({
                        ...f,
                        id: f.featureid,
                        name: f.title
                    }));

                    // Update current feature if one is selected
                    const selectedFeatureId = getSelectedFeatureId(selectedFeature);
                    const updatedCurrentFeature = selectedFeatureId ? findFeatureBySelectedId(filteredFeatures, selectedFeature) : null;

                    setDataState(prev => ({
                        ...prev,
                        features: filteredFeatures,
                        currentFeature: updatedCurrentFeature,
                        projectWithFeatures: {
                            ...prev.validatedProject,
                            features: transformedFeatures
                        }
                    }));

                    // Set loading to false first, then notify parent to clear delete state
                    // This ensures the skeleton stays visible until data is fully updated
                    setLoadingState(prev => ({ ...prev, isLoadingFeatures: false }));

                    // Features refreshed successfully - notify parent to clear delete loading state
                    onFeatureDeleteComplete?.();
                })
                .catch(() => {
                    setLoadingState(prev => ({ ...prev, isLoadingFeatures: false }));
                });
        }
    }, [featuresRefreshTrigger, dataState.validatedProject, token, loadingState.isInitializing, selectedFeature, onFeatureDeleteComplete, deletingFeatureId]);

    // Handle work product refresh trigger when navigating back from editor
    useEffect(() => {
        if (workProductRefreshTrigger && dataState.validatedProject && token && !loadingState.isInitializing) {
            const projectId = dataState.validatedProject.project_id;
            const numericProjectId = typeof projectId === "string" ? parseInt(projectId, 10) : projectId;
            const scopeKey = (scope: SddWorkProductScope): string => `${scope.level}:${scope.featureId ?? ""}:${scope.userStoryId ?? ""}`;
            const fetchScope = (scope: SddWorkProductScope): boolean => {
                if (!numericProjectId) return false;
                if (scope.level === "FEATURE" && !scope.featureId) return false;
                if (scope.level === "USERSTORY" && (!scope.featureId || !scope.userStoryId)) return false;
                dispatch(
                    fetchWorkProductsByPhase({
                        projectId: numericProjectId,
                        featureId: scope.level === "PRODUCT" ? undefined : (scope.featureId ?? undefined),
                        userStoryId: scope.level === "USERSTORY" ? (scope.userStoryId ?? undefined) : undefined,
                        level: scope.level
                    })
                );
                return true;
            };

            // Force clear the tracking refs to ensure a fresh fetch
            lastLoadedFeatureId.current = null;
            lastLoadedUserStoryIdRef.current = null;
            lastFetchParams.current = { featureId: null, userStoryId: null };

            const featureId = (selectedFeature as any)?.id || selectedFeature?.featureid;
            const numericFeatureId = typeof featureId === "string" ? parseInt(featureId, 10) : Number(featureId || 0) || null;
            const currentScope: SddWorkProductScope = selectedUserStory?.id
                ? {
                    level: "USERSTORY",
                    featureId: numericFeatureId,
                    userStoryId: String(selectedUserStory.id)
                }
                : numericFeatureId
                  ? {
                      level: "FEATURE",
                      featureId: numericFeatureId,
                      userStoryId: null
                  }
                  : {
                      level: "PRODUCT",
                      featureId: null,
                      userStoryId: null
                  };

            fetchScope(currentScope);

            if (workProductRefreshScope && scopeKey(workProductRefreshScope) !== scopeKey(currentScope)) {
                fetchScope(workProductRefreshScope);
            }

            if (numericProjectId && selectedFeature && numericFeatureId) {
                
                console.log("Work product refresh triggered - fetching at correct level:", {
                    featureId: numericFeatureId,
                    userStoryId: selectedUserStory?.id,
                    level: selectedUserStory?.id ? "USERSTORY" : "FEATURE"
                });

                // Update the tracking refs after fetch
                lastLoadedFeatureId.current = numericFeatureId;
                lastLoadedUserStoryIdRef.current = selectedUserStory?.id || null;
            }
        }
    }, [workProductRefreshScope, workProductRefreshTrigger, selectedFeature, selectedUserStory, dataState.validatedProject, token, loadingState.isInitializing, dispatch]);

    // Keep local expansion state aligned with parent-controlled hierarchy state.
    React.useEffect(() => {
        if (!hierarchyExpandedItems || hierarchyExpandedItems.length === 0) return;
        setExpandedNodes(new Set(hierarchyExpandedItems));
    }, [hierarchyExpandedItems]);

    // Sync selectedNodeKey with current selection
    React.useEffect(() => {
        if (!dataState.validatedProject) return;

        const productKey = `P:${dataState.validatedProject.project_id}`;
        let featureKey: string | null = null;

        if (selectedUserStory) {
            const featureId = selectedFeature?.id || selectedFeature?.featureid;
            if (featureId) {
                featureKey = `F:${featureId}`;
                setSelectedNodeKey(`S:${featureId}:${selectedUserStory.id}`);
            }
        } else if (selectedFeature) {
            const featureId = selectedFeature.id || selectedFeature.featureid;
            featureKey = `F:${featureId}`;
            setSelectedNodeKey(featureKey);
        } else {
            setSelectedNodeKey(productKey);
        }

        // Initialize expanded nodes with product
        applyExpandedNodes(prev => {
            const next = new Set(prev);
            next.add(productKey);
            if (featureKey) {
                next.add(featureKey);
            }
            return next;
        });
    }, [selectedFeature, selectedUserStory, dataState.validatedProject, applyExpandedNodes]);

    // Bring the selected tree row into view/focus so agent navigation mirrors manual clicks.
    React.useEffect(() => {
        if (!selectedNodeKey) return;

        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 4;

        const focusNode = () => {
            if (cancelled) return;
            const row = document.querySelector(`[data-tree-node-key="${selectedNodeKey}"]`) as HTMLElement | null;
            if (row) {
                row.scrollIntoView({ behavior: "smooth", block: "nearest" });
                row.focus({ preventScroll: true });
                return;
            }

            attempts += 1;
            if (attempts < maxAttempts) {
                window.setTimeout(focusNode, 75);
            }
        };

        const timeoutId = window.setTimeout(focusNode, 0);
        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [selectedNodeKey, expandedNodes, dataState.features, userStoriesByFeature]);

    // Debug effect to monitor work products changes
    useEffect(() => {
        console.log("=== FeatureStudioView workProductsByPhase changed ===");
        console.log("Current workProductsByPhase:", props.workProductsByPhase, dataState.validatedProject);
        console.log("Selected Feature:", selectedFeature);
        console.log("Selected User Story:", selectedUserStory);

        if (props.workProductsByPhase) {
            Object.keys(props.workProductsByPhase).forEach(phase => {
                const products = props.workProductsByPhase[phase] || [];
                console.log(
                    `${phase} phase (${products.length} items):`,
                    products.map(wp => ({
                        id: wp.id,
                        name: wp.name,
                        state: wp.state,
                        status: wp.status,
                        hasContent: wp.hasContent,
                        level: wp.level,
                        display_level: wp.display_level
                    }))
                );
            });
        }
        console.log("=== End workProductsByPhase change ===");
    }, [props.workProductsByPhase, selectedFeature, selectedUserStory]);

    const handleFeaturePromotion = async (data: ProjectSetupData) => {
        // Pre-fill the chatbot with feature details
        const enhancedData = {
            ...data,
            projectType: selectedFeature?.name || "New Project"
            // Add any other feature-specific data
        };

        if (onFeaturePromote) {
            onFeaturePromote(enhancedData);
        }

        setShowPromotionModal(false);
        // Navigate back to projects to show the new project
        onBackToProjects();
    };

    const getFeatureStatus = React.useCallback((feature: Feature | null) => {
        if (!feature) {
            return { label: "Unknown", color: "text.disabled", bgColor: "rgba(158, 158, 158, 0.1)" };
        }

        const statusMap = {
            proposed: { label: "Proposed", color: "info.main", bgColor: "rgba(33, 150, 243, 0.1)" },
            in_progress: { label: "In Progress", color: "primary.main", bgColor: "rgba(16, 54, 153, 0.1)" },
            review: { label: "Review", color: "warning.main", bgColor: "rgba(255, 193, 7, 0.1)" },
            completed: { label: "Completed", color: "success.main", bgColor: "rgba(76, 175, 80, 0.1)" },
            on_hold: { label: "On Hold", color: "text.disabled", bgColor: "rgba(158, 158, 158, 0.1)" }
        };

        return statusMap[feature.status as keyof typeof statusMap] || { label: feature.status, color: "text.disabled", bgColor: "rgba(158, 158, 158, 0.1)" };
    }, []);

    // Show loading state during initial load or when loading project
    if (loadingState.isInitializing || loadingState.isLoadingProject) {
        return (
            <MuiBox sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
                <MuiPaper
                    elevation={0}
                    sx={{
                        height: 64,
                        minHeight: 64,
                        maxHeight: 64,
                        display: "flex",
                        alignItems: "center",
                        px: 3,
                        borderBottom: 1,
                        borderColor: "divider",
                        borderRadius: 0,
                        bgcolor: "#F1F5FE",
                        flexShrink: 0
                    }}
                >
                    <MuiBox sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                        <NavigationBackButton
                            label="Back to Products"
                            onClick={onBackToProjects}
                            description="Navigate back to the products page"
                            bridgeId="btn.back-to-products"
                        />
                        <MuiTypography variant="h4" sx={{ fontWeight: 600, color: "text.primary", fontSize: "1.25rem" }}>
                            Feature Studio
                        </MuiTypography>
                        <MuiTypography variant="h4" sx={{ color: "text.disabled", fontWeight: 300, fontSize: "1.25rem" }}>
                            |
                        </MuiTypography>
                        <MuiTypography variant="body1" sx={{ color: "text.secondary", fontSize: "0.875rem", fontWeight: 400 }}>
                            Validating current product...
                        </MuiTypography>
                    </MuiBox>
                </MuiPaper>
                <MuiBox sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <MuiBox sx={{ textAlign: "center" }}>
                        <MuiCircularProgress sx={{ mb: 2 }} />
                        <MuiTypography variant="body1" color="text.secondary">
                            Loading current products...
                        </MuiTypography>
                    </MuiBox>
                </MuiBox>
            </MuiBox>
        );
    }

    // Show error state only if no valid project exists AND we're not loading
    if (!dataState.validatedProject && !loadingState.isInitializing && !loadingState.isLoadingProject) {
        return (
            <MuiBox sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
                <MuiPaper
                    elevation={0}
                    sx={{
                        height: 64,
                        minHeight: 64,
                        maxHeight: 64,
                        display: "flex",
                        alignItems: "center",
                        px: 3,
                        borderBottom: 1,
                        borderColor: "divider",
                        borderRadius: 0,
                        bgcolor: "#F1F5FE",
                        flexShrink: 0
                    }}
                >
                    <MuiBox sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                        <NavigationBackButton
                            label="Back to Products"
                            onClick={onBackToProjects}
                            description="Navigate back to the products page"
                            bridgeId="btn.back-to-products"
                        />
                        <MuiTypography variant="h4" sx={{ fontWeight: 600, color: "text.primary", fontSize: "1.25rem" }}>
                            Feature Studio
                        </MuiTypography>
                        <MuiTypography variant="h4" sx={{ color: "text.disabled", fontWeight: 300, fontSize: "1.25rem" }}>
                            |
                        </MuiTypography>
                        <MuiTypography variant="body1" sx={{ color: "error.main", fontSize: "0.875rem", fontWeight: 500 }}>
                            No Product Available
                        </MuiTypography>
                    </MuiBox>
                </MuiPaper>
                <MuiBox sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <MuiBox sx={{ textAlign: "center", maxWidth: 500, px: 3 }}>
                        <MuiTypography variant="h5" sx={{ mb: 2, color: "error.main" }}>
                            No Product Found
                        </MuiTypography>
                        <MuiTypography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            Feature Studio requires a product to be selected. Please go to the Products page and select a product to continue.
                        </MuiTypography>
                        <MuiButton variant="contained" onClick={onBackToProjects} sx={{ px: 4 }}>
                            Go to Products
                        </MuiButton>
                    </MuiBox>
                </MuiBox>
            </MuiBox>
        );
    }

    return (
        <MuiBox
            sx={{
                height: "100vh",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.default",
                "@supports (height: 100dvh)": { height: "100dvh" }
            }}
        >
            {/* Page header compacts on laptop-sized viewports to preserve workspace height. */}
            <MuiPaper
                elevation={0}
                sx={{
                    height: 64,
                    minHeight: 64,
                    maxHeight: 64,
                    display: "flex",
                    alignItems: "center",
                    px: 3,
                    borderBottom: 1,
                    borderColor: "divider",
                    borderRadius: 0,
                    bgcolor: "#F1F5FE",
                    flexShrink: 0,
                    [LAPTOP_FIT_QUERY]: { height: 56, minHeight: 56, maxHeight: 56, px: 2 }
                }}
            >
                <MuiBox sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                    <NavigationBackButton
                        label={sddSubpageOpen ? "Back to Feature Studio" : "Back to Products"}
                        onClick={handleFeatureStudioBack}
                        description={sddSubpageOpen ? "Return to Feature Studio" : "Navigate back to the products page"}
                        bridgeId={sddSubpageOpen ? "btn.back-to-feature-studio" : "btn.back-to-products"}
                    />

                    <MuiTypography variant="h4" sx={{ fontWeight: 600, color: "text.primary", fontSize: "1.25rem", whiteSpace: "nowrap" }}>
                        Feature Studio
                    </MuiTypography>

                    <MuiTypography variant="h4" sx={{ color: "text.disabled", fontWeight: 300, fontSize: "1.25rem" }}>
                        |
                    </MuiTypography>

                    {(dataState.projectWithFeatures || dataState.validatedProject) && (
                        <>
                            <MuiTypography variant="body1" sx={{ color: "text.primary", fontSize: "0.875rem", fontWeight: 500, whiteSpace: "nowrap" }}>
                                {(dataState.projectWithFeatures || dataState.validatedProject)?.project_name ||
                                    (dataState.projectWithFeatures || dataState.validatedProject)?.name}
                            </MuiTypography>
                        </>
                    )}
                </MuiBox>
                {(batchPending || activeProductBatch) && (
                    <MuiChip
                        size="small"
                        color={batchStatus === "complete" ? "success" : batchStatus === "partial" ? "error" : "primary"}
                        label={
                            activeProductBatch?.counts.total
                                ? `${activeProductBatch.counts.completed}/${activeProductBatch.counts.total}`
                                : batchPending
                                  ? "Checking"
                                  : activeProductBatch?.phase
                        }
                        sx={{ ml: 1 }}
                    />
                )}
            </MuiPaper>

            {(batchPending || batchError || activeProductBatch) && (
                <MuiAlert
                    severity={batchError ? "error" : batchStatus === "complete" ? "success" : batchStatus === "partial" ? "warning" : "info"}
                    icon={batchStatus === "complete" ? <CheckCircleIcon /> : undefined}
                    action={
                        batchError || batchStatus === "partial" ? (
                            <MuiButton size="small" onClick={generateAllWorkProducts}>
                                Retry batch
                            </MuiButton>
                        ) : activeProductBatch ? (
                            <MuiButton size="small" onClick={() => openBackgroundActivity()}>
                                View details
                            </MuiButton>
                        ) : undefined
                    }
                    sx={{ borderRadius: 0, py: 0.5, flexShrink: 0 }}
                >
                    {batchPending ? "Validating Context Mesh requirements…" : batchError || activeProductBatch?.message}
                    {activeProductBatch && activeProductBatch.counts.total > 0 && batchStatus !== "complete" && (
                        <MuiLinearProgress
                            variant="determinate"
                            value={
                                ((activeProductBatch.counts.completed + activeProductBatch.counts.failed + activeProductBatch.counts.cancelled) /
                                    activeProductBatch.counts.total) *
                                100
                            }
                            aria-label="Generate All Work Products progress"
                            sx={{ mt: 0.5, minWidth: 240 }}
                        />
                    )}
                </MuiAlert>
            )}

            <MuiDrawer anchor="right" open={showReadinessDrawer} onClose={() => setShowReadinessDrawer(false)}>
                <MuiBox sx={{ width: { xs: 320, sm: 420 }, p: 3 }} role="dialog" aria-labelledby="context-readiness-title">
                    <MuiTypography id="context-readiness-title" variant="h6" sx={{ fontWeight: 700 }}>
                        Context Mesh needs attention
                    </MuiTypography>
                    <MuiTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Complete these inputs before generating all Work Products. Individual Work Products can still be generated from their cards.
                    </MuiTypography>
                    <MuiList sx={{ mt: 2 }}>
                        {readinessIssues.map(issue => (
                            <MuiListItem key={issue} alignItems="flex-start" sx={{ px: 0 }}>
                                <MuiListItemIcon sx={{ minWidth: 34, mt: 0.25 }}>
                                    <ErrorOutlineIcon color="warning" fontSize="small" />
                                </MuiListItemIcon>
                                <MuiListItemText primary={issue} />
                            </MuiListItem>
                        ))}
                    </MuiList>
                    <MuiButton fullWidth variant="contained" onClick={() => setShowReadinessDrawer(false)}>
                        Return to Feature Studio
                    </MuiButton>
                </MuiBox>
            </MuiDrawer>

            {/* Body Content */}
            <MuiBox sx={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0, position: "relative" }}>
                {/* Collapsible Product → Feature → User Story hierarchy rail */}
                {SHOW_HIERARCHY_RAIL && (
                    <MuiBox
                        sx={{
                            width: isHierarchyCollapsed ? 44 : `${leftPanelWidth}px`,
                            flex: `0 0 ${isHierarchyCollapsed ? 44 : leftPanelWidth}px`,
                            height: "100%",
                            display: "flex",
                            position: "relative",
                            bgcolor: "background.paper",
                            borderRight: isHierarchyCollapsed ? "1px solid" : 0,
                            borderColor: "divider",
                            transition: "width 180ms ease, flex-basis 180ms ease"
                        }}
                    >
                        {isHierarchyCollapsed ? (
                            <MuiTooltip title="Expand product hierarchy" placement="right">
                                <MuiIconButton
                                    onClick={onHierarchyToggle}
                                    aria-label="Expand product hierarchy"
                                    aria-expanded="false"
                                    size="small"
                                    sx={{
                                        position: "absolute",
                                        top: 12,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        width: 30,
                                        height: 30,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        bgcolor: "background.paper",
                                        color: "text.secondary",
                                        "&:hover": { bgcolor: "action.hover", color: "primary.main" }
                                    }}
                                >
                                    <ChevronRightIcon fontSize="small" />
                                </MuiIconButton>
                            </MuiTooltip>
                        ) : (
                            <>
                                <GuidedTiersNav
                                    product={{
                                        ...dataState.validatedProject,
                                        features: dataState.features
                                    }}
                                    selectedKey={selectedNodeKey}
                                    onSelect={handleTreeSelect}
                                    expanded={expandedNodes}
                                    onToggleExpand={handleTreeToggleExpand}
                                    onAddStory={onAddUserStory}
                                    onAddFeature={onAddFeature}
                                    width={leftPanelWidth}
                                    workProductsByPhase={props.workProductsByPhase}
                                    batchStatus={batchStatus}
                                />
                                <MuiTooltip title="Collapse product hierarchy" placement="right">
                                    <MuiIconButton
                                        onClick={onHierarchyToggle}
                                        aria-label="Collapse product hierarchy"
                                        aria-expanded="true"
                                        size="small"
                                        sx={{
                                            position: "absolute",
                                            right: -14,
                                            top: 12,
                                            zIndex: 3,
                                            width: 28,
                                            height: 28,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            bgcolor: "background.paper",
                                            color: "text.secondary",
                                            boxShadow: "0 1px 3px rgba(15,23,42,.12)",
                                            "&:hover": { bgcolor: "action.hover", color: "primary.main" }
                                        }}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </MuiIconButton>
                                </MuiTooltip>
                                {/* Resize Handle */}
                                <MuiBox
                                    onMouseDown={handleMouseDown}
                                    sx={{
                                        width: "4px",
                                        height: "100%",
                                        position: "absolute",
                                        right: 0,
                                        top: 0,
                                        cursor: "col-resize",
                                        bgcolor: "transparent",
                                        "&:hover": {
                                            bgcolor: "primary.main",
                                            opacity: 0.5
                                        },
                                        "&:active": {
                                            bgcolor: "primary.main",
                                            opacity: 0.8
                                        },
                                        transition: "background-color 0.2s"
                                    }}
                                />
                            </>
                        )}
                    </MuiBox>
                )}

                <MuiBox sx={{ flex: 1, minWidth: 0, height: "100%", overflow: "hidden", bgcolor: "background.paper", display: "flex", flexDirection: "column" }}>
                    {/* Detail Header - Show when Feature or Story selected */}
                    {selectionData && selectionData.kind !== "Product" && !(featureStudioTab === "sdd" && sddScopePickerOpen) && (
                        <FeatureStudioDetailHeader kind={selectionData.kind} title={selectionData.title} subtitle={selectionData.subtitle} />
                    )}

                    {featureStudioTab !== "sdd" && onOpenMeshStrength && stats && dataState.features.length > 0 && (
                        <MeshStrengthEntryCard projectId={selectedProject?.project_id ?? null} onOpen={onOpenMeshStrength} />
                    )}

                    {/* Stats Strip */}
                    {stats && dataState.features.length > 0 && featureStudioTab === "discovery" && (
                        <FeatureStudioStats level={selectionData?.level || "product"} stats={stats} />
                    )}

                    {/* Empty state when project has no features */}
                    {featureStudioTab !== "sdd" &&
                        (dataState.projectWithFeatures || dataState.validatedProject) &&
                        dataState.features.length === 0 &&
                        !loadingState.isLoadingFeatures && (
                            <MuiBox sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
                                <MuiBox sx={{ textAlign: "center", maxWidth: 500 }}>
                                    <MuiTypography variant="h5" sx={{ mb: 2, color: "text.secondary" }}>
                                        No Features Available
                                    </MuiTypography>
                                    <MuiTypography variant="body1" color="text.disabled" sx={{ mb: 3 }}>
                                        This product doesn't have any features yet. Create your first feature to start working with the SDLC matrix and work
                                        products.
                                    </MuiTypography>
                                    <MuiBox sx={{ display: "flex", gap: 2 }}>
                                        <FeatureFlag name="add-feature">
                                            <MuiButton variant="contained" onClick={onAddFeature} sx={{ px: 4 }}>
                                                Add Feature
                                            </MuiButton>
                                        </FeatureFlag>
                                    </MuiBox>
                                </MuiBox>
                            </MuiBox>
                        )}

                    {/* Loading state while fetching features */}
                    {/* {loadingState.isLoadingFeatures && (
                        <MuiBox sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
                            <MuiBox sx={{ textAlign: "center" }}>
                                <MuiCircularProgress sx={{ mb: 2 }} />
                                <MuiTypography variant="body1" color="text.secondary">
                                    Loading Project/Features/User Stories...
                                </MuiTypography>
                            </MuiBox>
                        </MuiBox>
                    )} */}

                    {/* Tab Content - Show for both Product level and Feature level */}
                    {(featureStudioTab === "sdd" || dataState.features.length > 0) && (
                        <MuiBox sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                            <MemoizedTabContent
                                {...props}
                                handleAddWorkProductToEditable={wrappedHandleAddWorkProductToEditable}
                                currentFeature={dataState.currentFeature}
                                isLoadingFeature={loadingState.isLoadingFeature}
                                workProductsByPhase={props.workProductsByPhase}
                                isLoadingWorkProducts={workProductsLoadingStatus === "loading"}
                                validatedProject={dataState.projectWithFeatures || dataState.validatedProject}
                                viewMode={workProductViewMode}
                                onViewModeChange={setWorkProductViewMode}
                                getWorkProductState={getWorkProductState}
                                handleCardGenerate={handleCardGenerate}
                                onSyncClick={setSyncFeatureId}
                                handleCardOpen={handleCardOpen}
                                selectedNodeKey={selectedNodeKey}
                                hierarchyFeatures={dataState.features}
                                hierarchyStoriesByFeature={userStoriesByFeature}
                                sddHierarchyPageOpen={sddHierarchyPageOpen}
                                onSddHierarchyPageOpenChange={setSddHierarchyPageOpen}
                                sddScopePickerOpen={sddScopePickerOpen}
                                onSddScopePickerOpenChange={setSddScopePickerOpen}
                                sddProgressReportOpen={sddProgressReportOpen}
                                onSddProgressReportOpenChange={setSddProgressReportOpen}
                            />
                        </MuiBox>
                    )}
                </MuiBox>
            </MuiBox>

            {/* Feature Promotion Modal */}
            <ProjectSetupChatbotModal open={showPromotionModal} onClose={() => setShowPromotionModal(false)} onProjectSetup={handleFeaturePromotion} />

            {/* Jira/Azure DevOps Sync Panel — feature scope */}
            {syncFeatureId !== null && (
                <SyncPanel
                    open={syncFeatureId !== null}
                    onClose={() => setSyncFeatureId(null)}
                    scope={{ type: "feature", id: syncFeatureId }}
                    onNavigateToIntegrations={onNavigateToIntegrations}
                />
            )}
        </MuiBox>
    );
}
